import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTab, MatTabChangeEvent } from '@angular/material/tabs';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { faEdit, faPlus, faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DateTime } from 'luxon';
import { ToastrService } from 'ngx-toastr';
import { finalize, forkJoin, take } from 'rxjs';
import { QualificationResponse, qualificationNotice } from '../qualify-order';
import * as L from 'leaflet';

import { ConfirmDialogComponent } from 'src/app/confirm-dialog/confirm-dialog.component';
import {
  ContactAutocompleteResult,
  ContactAutocompleteService
} from '../../services/contacts/contact-autocomplete.service';
import * as SelectOptions from '../select-options';
import { ChangingCarrierDialogComponent } from './changing-carrier-dialog/changing-carrier-dialog.component';
import { ChangingConsigneeDialogComponent } from './changing-consignee-dialog/changing-consignee-dialog.component';
import { ChangingShipperDialogComponent } from './changing-shipper-dialog/changing-shipper-dialog.component';
import { DocumentModalComponent } from './document-modal/document-modal.component';
import { DocumentTypeSelectionDialog2 } from './document-type-selection-dialog2';
import {
  AddressInfo,
  ContactDetails,
  DocumentInfo,
  LadingNote,
  LadingLabourReview,
  LadingProductImage,
  LadingProductDeliveryResponse,
  LadingResponse,
  PackageInfo,
  ProductInfo,
  SignatureInfo,
  SiteCheckInfo,
  StatusTimelineInfo,
  SubcontactInfo,
  TgDeliveryReq,
  WorkOrderStatusTimelinePayload,
  WorkOrderStatusTimelineResponse
} from './work-order.interfaces';
import { WorkOrderService } from './work-order.service';
import { AuthService } from '../../auth.service';
import { canViewUfosUser } from '../ufos/ufos.types';

// Interface for status timeline form values
interface StatusTimelineFormValue {
  status: string;
  createdAt: string | null;
  modifiedAt: string | null;
  eventDate: string | null;
  eventTime: string | null;
  eventTimeZone: string;
  dateLocal: string | null;
  timeLocal: string | null;
  localTimeZone: string;
  localOverride: boolean;
  ladingUuid: string | null;
  timelineUuid: string | null;
  note: string | null;
  isCurrent: boolean;
}

interface StatusMapPoint {
  id: string;
  status: string;
  lat: number;
  lng: number;
  time: string;
}

interface NoteAudienceOption {
  value: string;
  label: string;
}

interface OperationFlagOption {
  controlName: string;
  label: string;
}

interface LabourReviewApprovalResponse {
  success?: boolean;
  message?: string;
  code?: string | number;
  data?: {
    updated_review?: LadingLabourReview;
    action_result?: string;
    error_message?: string;
  };
  raw?: {
    data?: {
      updated_review?: LadingLabourReview;
      action_result?: string;
      error_message?: string;
    };
  };
}

@Component({
  selector: 'app-work-order-details',
  templateUrl: './work-order-details.component.html',
  styleUrls: ['./work-order-details.component.scss']
})
export class WorkOrderDetailsComponent implements OnInit, AfterViewInit {
  // FontAwesome icons
  faTrash = faTrash;
  faEdit = faEdit;
  faSave = faSave;
  faTimes = faTimes;
  faPlus = faPlus;

  // UI state
  isLoading = false;
  isDraggingOverDocument = false;
  openingDocumentUuid: string | null = null;
  downloadingDocumentUuid: string | null = null;

  // Data sources
  documentsTable = new MatTableDataSource<DocumentInfo>();
  productImagesTable = new MatTableDataSource<LadingProductImage>();
  ladingNotes: LadingNote[] = [];
  signatures: SignatureInfo[] = [];
  packages: PackageInfo[] = [];
  products: ProductInfo[] = [];
  deliveryRequirements: TgDeliveryReq[] = [];
  ladingLabourReviews: LadingLabourReview[] = [];
  hasExpectedProducts = true;
  showWhiteGloveTab = false;

  readonly documentTypeLabels: Record<number, string> = {
    1: 'POD',
    2: 'BOL / Manifest',
    3: 'Delivery Receipt',
    4: 'Walk the Path',
    5: 'Carrier Confirmation',
    6: 'Carrier Invoice',
    7: 'Customs',
    8: 'Damage Claim',
    9: 'Other',
    10: 'Packing Slip',
    11: 'Pickup',
    12: 'Scale Ticket',
    13: 'Weight Certificate',
    14: 'Email',
    15: 'Lumper Receipt',
    16: 'Marshalling',
    17: 'Photos',
    18: 'Credit Card Receipt'
  };

  // Select options
  ladingStatuses: string[] = [];
  equipmentTypes: { code: string; name: string }[] = [];
  noteAudienceOptions: NoteAudienceOption[] = [{ value: '', label: 'All Parties' }];
  private defaultNoteContactUuid = '';
  weightUnits: { value: string; label: string }[] = [
    { value: 'lb', label: 'LB' },
    { value: 'kg', label: 'KG' }
  ];
  lengthUnits: { value: string; label: string }[] = [
    { value: 'in', label: 'IN' },
    { value: 'ft', label: 'FT' },
    { value: 'cm', label: 'CM' },
    { value: 'm', label: 'M' }
  ];
  timezones: string[] = [
    'America/New_York', 'America/Toronto', 'America/Detroit', 'America/Indiana/Indianapolis',
    'America/Chicago', 'America/Winnipeg', 'America/Denver', 'America/Edmonton',
    'America/Phoenix', 'America/Los_Angeles', 'America/Vancouver', 'America/Anchorage',
    'Pacific/Honolulu', 'America/Argentina/Buenos_Aires', 'America/Sao_Paulo',
    'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Moscow', 'Africa/Cairo',
    'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo',
    'Australia/Sydney', 'Australia/Adelaide', 'Australia/Perth', 'Pacific/Auckland', 'Pacific/Fiji'
  ];
  dropTrailerStatuses: string[] = [
    'Dropped DC',
    'Loaded DC',
    'Picked-up DC',
    'Dropped Pool',
    'Pool Offload Started',
    'Pool Offload Finished',
    'Pool Scanning Started',
    'Pool Scanning Finished'
  ];
  operationFlagOptions: OperationFlagOption[] = [
    { controlName: 'carrierRated', label: 'Carrier Rated' },
    { controlName: 'carrierRerated', label: 'Carrier Rerated' },
    { controlName: 'strapsBars', label: 'Straps/Bars' },
    { controlName: 'liftLull', label: 'Lift/Lull' },
    { controlName: 'callSite', label: 'Call Site' },
    { controlName: 'protectiveEquipment', label: 'Protective Equipment' },
    { controlName: 'unmannedJobSite', label: 'Unmanned Job Site' },
    { controlName: 'loadedOdot', label: 'Loaded ODOT' },
    { controlName: 'loadingDelay', label: 'Loading Delay' },
    { controlName: 'documentDelay', label: 'Document Delay' },
    { controlName: 'borderDelay', label: 'Border Delay' },
    { controlName: 'customsInspection', label: 'Customs Inspection' },
    { controlName: 'trailerUnavailable', label: 'Trailer Unavailable' },
    { controlName: 'driverUnavailable', label: 'Driver Unavailable' },
    { controlName: 'driverHos', label: 'Driver HOS' },
    { controlName: 'carrierReneged', label: 'Carrier Reneged' },
    { controlName: 'mechanicalDelay', label: 'Mechanical Delay' },
    { controlName: 'trailerSwitch', label: 'Trailer Switch' },
    { controlName: 'weatherDelay', label: 'Weather Delay' },
    { controlName: 'dockWaiting', label: 'Dock Waiting' },
    { controlName: 'dockReDelivery', label: 'Dock Re-Delivery' },
    { controlName: 'scheduledHoliday', label: 'Scheduled Holiday' }
  ];

  // Forms
  generalInfoForm!: FormGroup;
  shipperInfoForm!: FormGroup;
  consigneeInfoForm!: FormGroup;
  customerInfoForm!: FormGroup;
  carrierInfoForm!: FormGroup;
  operationInfoForm!: FormGroup;
  customerCareInfoForm!: FormGroup;
  salesInfoForm!: FormGroup;
  administrationInfoForm!: FormGroup;
  claimInfoForm!: FormGroup;
  technicianInfoForm!: FormGroup;
  labourReviewForms!: FormArray;
  statusTimelineForms!: FormArray;

  // Original status values for resetting
  private originalStatusValues: StatusTimelineFormValue[] = [];
  @ViewChild('statusMapContainer') statusMapContainer?: ElementRef<HTMLDivElement>;
  @ViewChildren('noteTextarea') noteTextareas?: QueryList<ElementRef<HTMLTextAreaElement>>;
  @ViewChildren(MatTab) workOrderTabs?: QueryList<MatTab>;
  selectedWorkOrderTabIndex = 0;
  activeTabLabel = 'General Info';
  statusMapLoaded = false;
  statusMapPoints: StatusMapPoint[] = [];
  statusSaveInProgress = false;
  private statusTabActive = false;
  private statusMap: L.Map | null = null;
  private readonly statusMapApiUrl = '/api/dynamic_grids/lading_map_data.php';
  private readonly deliveredConsigneeStatus = 'Delivered Consignee';
  private readonly browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  private readonly labourReviewApprovalApiUrl = '/api/work_orders/labour_review_approval.php';
  private activeLabourReviewApprovalUuid: string | null = null;
  private requestedWorkOrderTabSlug: string | null = null;

  // Installation form links
  installationFormLinks: { [key: string]: string } = {
    inservice: 'https://inservice.ulaap.com/',
    checklist: 'https://checklist.ulaap.com/',
    disclaimer: 'https://disclaimer.ulaap.com/',
    inagreement: 'https://inagreement.ulaap.com/',
    sitesurvey: 'https://sitesurvey.ulaap.com/'
  };

  // Lading UUID from route
  billing: any = null;
  billingChoice = '';
  billingSaving = false;
  billingMessage = '';
  loadBilling(): void {
    if (!this.ladingUuid) return;
    const id = this.ladingUuid;
    this.httpClient.get<any>('/api/work_orders/customer_billing.php', { params: { lading_uuid: id } }).subscribe({
      next: result => { if (id !== this.ladingUuid) return; this.billing = result; this.billingChoice = result.billing_account_uuid || ''; },
      error: () => { this.billing = null; }
    });
  }
  saveBilling(): void {
    if (!this.billing || this.billingSaving) return;
    this.billingSaving = true; this.billingMessage = '';
    this.httpClient.post<any>('/api/work_orders/customer_billing.php', {
      lading_uuid: this.ladingUuid, billing_account_uuid: this.billingChoice, revision: this.billing.revision
    }).pipe(finalize(() => this.billingSaving = false)).subscribe({
      next: result => { this.billing = result; this.billingChoice = result.billing_account_uuid || ''; this.billingMessage = result.pending ? 'Saved. UFOs billing update is pending; do not resubmit.' : 'Billing account saved.'; },
      error: error => { this.billingMessage = error.error?.message || 'Check the current billing selection before retrying.'; this.loadBilling(); }
    });
  }
  private ladingUuid: string | null = null;
  public alternate_reference: string | null = null;
  qualificationInProgress = false;
  qualificationMessage = "";
  canViewUfos = false;
  canViewFinancialHistory = false;
  canViewWorkOrderHistory = false;

  public get workOrderLadingUuid(): string | null {
    return this.ladingUuid;
  }

  // Timezone information
  private shipperTimezone: string | null = 'America/Toronto';
  private consigneeTimezone: string | null = 'America/Toronto';

  // Address display text
  shipperAddressText: string = '';
  consigneeAddressText: string = '';
  customerAddressText: string = ''; //requested by Allan Aug 14 2025

  constructor(
    private httpClient: HttpClient,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private router: Router,
    private toastrService: ToastrService,
    private workOrderService: WorkOrderService,
    private contactSvc: ContactAutocompleteService,
    private authService: AuthService
  ) {
    this.initializeForms();
    this.preventDefaultDragAndDrop();
  }

  ngOnInit(): void {
    this.authService.loadCurrentUser().pipe(take(1)).subscribe({
      next: user => {
        this.canViewUfos = canViewUfosUser(user?.user_id);
        this.canViewFinancialHistory = user?.permissions.includes('financials.view') ?? false;
        // Jenny: staff can view operational history without financial access.
        // These flags control the tab; the PHP helper and API enforce permissions.
        const roles = user?.roles ?? [];
        const privilegedRoles = ['ADMIN', 'SUPER_ADMIN', 'MASTER'];
        const internalRoles = [
          'ADMIN',
          'SUPER_ADMIN',
          'MASTER',
          'AGENT',
          'CUSTOMER_CARE',
          'DISPATCHER',
          'FINANCE',
          'OPERATIONS',
          'SALES',
          'SHIPPING_RECEIVING',
          'MANAGER',
        ];
        const externalRoles = ['CARRIER', 'CONSIGNEE', 'CUSTOMER', 'DRIVER', 'SHIPPER'];

        const privileged = roles.some(role => privilegedRoles.includes(role));
        const internal = roles.some(role => internalRoles.includes(role));
        const external = roles.some(role => externalRoles.includes(role));
        this.canViewWorkOrderHistory = internal && (privileged || !external);
      },
      error: () => {
        this.canViewUfos = false;
        this.canViewFinancialHistory = false;
        this.canViewWorkOrderHistory = false;
      }
    });
    this.isLoading = true;
    this.ladingStatuses = [...SelectOptions.STATUSES];
    if (!this.timezones.includes(this.browserTimezone)) {
      this.timezones.push(this.browserTimezone);
    }
    this.equipmentTypes = Object.entries(SelectOptions.TRAILER_TYPES).map(([code, name]) => ({ code, name }));
    this.setupFormValueChanges();

    this.activatedRoute.queryParamMap.subscribe(params => {
      this.requestedWorkOrderTabSlug = this.normalizeTabSlug(params.get('tab'));
      this.selectRequestedWorkOrderTab();
    });

    this.activatedRoute.paramMap.subscribe(params => {
      this.ladingUuid = params.get('uuid');
      if (!this.ladingUuid) {
        this.toastrService.error('No UUID provided.');
        this.isLoading = false;
        return;
      }
      this.loadData();
      this.fetchInstallationFormLinks();
    });
  }

  ngAfterViewInit(): void {
    // The tab query becomes available after Angular has checked selectedIndex.
    // Apply deep-link selection on the next turn to avoid changing it mid-check.
    const selectAvailableTab = () => queueMicrotask(() => this.selectRequestedWorkOrderTab());
    this.workOrderTabs?.changes.subscribe(selectAvailableTab);
    selectAvailableTab();
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.selectedWorkOrderTabIndex = event.index;
    this.activeTabLabel = event.tab.textLabel;
    this.statusTabActive = event.tab.textLabel === 'Status';
    if (this.statusTabActive) {
      this.loadStatusMapData();
    }

    const tabSlug = this.normalizeTabSlug(event.tab.textLabel);
    if (tabSlug && tabSlug !== this.requestedWorkOrderTabSlug) {
      this.requestedWorkOrderTabSlug = tabSlug;
      void this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { tab: tabSlug },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
  }

  private selectRequestedWorkOrderTab(): void {
    if (!this.requestedWorkOrderTabSlug || !this.workOrderTabs) return;

    const tabs = this.workOrderTabs.toArray();
    const requestedIndex = tabs.findIndex(tab =>
      this.normalizeTabSlug(tab.textLabel) === this.requestedWorkOrderTabSlug
    );

    if (requestedIndex >= 0) {
      this.selectedWorkOrderTabIndex = requestedIndex;
      this.activeTabLabel = tabs[requestedIndex].textLabel;
    }
  }

  private normalizeTabSlug(value: string | null | undefined): string | null {
    const normalized = String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return normalized || null;
  }

  // Initialize all form groups
  private initializeForms(): void {
    this.generalInfoForm = this.formBuilder.group({
      proBill: [''],
      ladingNumber: [''],
      customerName: [''],
      shipperName: [''],
      consigneeName: [''],
      carrierName: [''],
      alternateReference: [''],
      ladingStatus: [''],
      tripNumber: [''],
      dropTrailerStatus: [''],
      hot: [false],
      expedited: [false],
      dropTrailer: [false],
      partialOrFull: [''],
      ladingId: [''],
      shipmentNumber: [''],
      thirdPartyTrackingUrl: [''],
      loadWeight: ['0'],
      loadWeightUnit: [''],
      loadWeightActual: ['0'],
      loadLength: ['0'],
      loadLengthUnit: [''],
      loadPallets: ['0'],
      loadPieces: ['0'],
      typeOfEquipment: [''],
      appointmentDate: [''],
      appointmentStart: [''],
      appointmentEnd: [''],
      appointmentTimezone: [this.consigneeTimezone],
      loadStartDate: [''],
      loadStartTime: ['', Validators.pattern('^([01]\\d|2[0-3]):([0-5]\\d)$')],
      loadEndDate: [''],
      loadEndTime: ['', Validators.pattern('^([01]\\d|2[0-3]):([0-5]\\d)$')],
      loadSpecialInfo: [''],
      ladingUuid: [''],
      carrierTrailer: [''],
      customerPo: [''],
      weekNo: [''],
      created: [''],
      modifiedDate: [''],
      modifiedTime: [''],
      estimatedLoadStart: [''],
      estimatedLoadStartHours: [''],
      estimatedLoadEnd: [''],
      estimatedLoadEndHours: ['']
    });

    this.technicianInfoForm = this.formBuilder.group({
      contactUuid: [''],
      contactOrganization: [''],
      contactNameGiven: [''],
      contactNameMiddle: [''],
      contactNameFamily: [''],
      contactNickname: [''],
      contactEmail: ['']
    });

    this.shipperInfoForm = this.formBuilder.group({
      contactType: ['SHIPPER'],
      ladingUuid: [''],
      contactUuid: [''],
      domainUuid: [''],
      contactOrganization: [''],
      contactNameGiven: [''],
      contactNameMiddle: [''],
      contactNameFamily: [''],
      contactEmail: [''],
      usDotNo: [''],
      active: [true],
      carrierMc: [''],
      poolId: [''],
      routeCode: [''],
      dropTrailerParty: [false],
      pickupReferenceNumberRequired: [false],
      pickupReferenceNumber: [''],
      loadingDock: [false],
      pickupTailgate: [false],
      truckSpace: [false],
      appointment: [false],
      callSite: [false],
      shippingBolRequired: [false],
      residential: [false],
      commercial: [false],
      jobSite: [false],
      unmannedJobSite: [false],
      notificationAtPickup: [false],
      notificationDelayedPickup: [false],
      notificationAtDelivery: [false],
      notificationDelayedDelivery: [false],
      notificationAssessorialsIncured: [false],
      jobSiteContactName: [''],
      jobSiteContactPhone: [''],
      appointmentContactName: [''],
      appointmentContactPhone: [''],
      subcontactInfo: this.formBuilder.array([])
    });

    this.consigneeInfoForm = this.formBuilder.group({
      ladingUuid: [''],
      contactType: ['CONSIGNEE'],
      contactUuid: [''],
      ladingSiteCheckUuid: [''],
      deliveryReferenceNumberRequired: [false],
      deliveryReferenceNumber: [''],
      appointment: [false],
      loadingDock: [false],
      pickupTailgate: [false],
      truckSpace: [false],
      callSite: [false],
      commercial: [false],
      residential: [false],
      jobSite: [false],
      unmannedJobSite: [false],
      conventionCenter: [false],
      popUpStore: [false],
      notificationAtPickup: [false],
      notificationDelayedPickup: [false],
      notificationAtDelivery: [false],
      notificationDelayedDelivery: [false],
      notificationAssessorialsIncured: [false],
      jobSiteContactName: [''],
      jobSiteContactPhone: [''],
      appointmentContactName: [''],
      appointmentContactPhone: [''],
      subcontactInfo: this.formBuilder.array([])
    });

    this.customerInfoForm = this.formBuilder.group({
      contactType: ['CUSTOMER'],
      contactUuid: [''],
      ladingSiteCheckUuid: [''],
      ladingUuid: [''],
      bolRequired: [false],
      invoiceBolRequired: [false],
      rateQuoteRequired: [false],
      invoicePoRequired: [false],
      invoicePo: [''],
      notificationAtPickup: [false],
      notificationDelayedPickup: [false],
      notificationAtDelivery: [false],
      notificationDelayedDelivery: [false],
      notificationAccessorialsIncured: [false],
      subcontactInfo: this.formBuilder.array([]),
      contactAddress: ['']
    });

    this.carrierInfoForm = this.formBuilder.group({
      contactUuid: [''],
      organization: [''],
      name: [''],
      streetAddress: [''],
      city: [''],
      postalCode: [''],
      country: [''],
      streetAddressExtended: [''],
      documentCategory: [''],
      documentType: [''],
      subcontactInfo: this.formBuilder.array([])
    });

    this.operationInfoForm = this.formBuilder.group({
      ladingOperationUuid: [''],
      carrierRated: [false],
      carrierRerated: [false],
      strapsBars: [false],
      liftLull: [false],
      callSite: [false],
      protectiveEquipment: [false],
      unmannedJobSite: [false],
      operationsNotation: [''],
      loadedOdot: [false],
      loadingDelay: [false],
      documentDelay: [false],
      borderDelay: [false],
      customsInspection: [false],
      trailerUnavailable: [false],
      driverUnavailable: [false],
      driverHos: [false],
      carrierReneged: [false],
      mechanicalDelay: [false],
      trailerSwitch: [false],
      weatherDelay: [false],
      dockWaiting: [false],
      dockReDelivery: [false],
      scheduledHoliday: [false]
    });

    this.customerCareInfoForm = this.formBuilder.group({
      ladingCareUuid: [''],
      confirmPickUp: [false],
      confirmDelivery: [false],
      podRequired: [false],
      purchaseOrderRequired: [false],
      careNotation: ['']
    });

    this.salesInfoForm = this.formBuilder.group({
      ladingSaleUuid: [''],
      quoteRequired: [false],
      requoteRequired: [false],
      customerRated: [false],
      salesNotation: [''],
      managementNotation: ['']
    });

    this.administrationInfoForm = this.formBuilder.group({
      ladingAdministrationUuid: [''],
      poAudited: [false],
      podAudited: [false],
      confirmCharges: [false],
      carrierInvoiced: [false],
      freightCostApproved: [false],
      freightCostException: [false],
      readyInvoice: [false],
      damageClaim: [false],
      intentClaim: [false],
      claimReview: [false],
      invoiceThanks: [false],
      invoiceDue: [false],
      invoiceCorrect: [false],
      invoiceLate: [false],
      carrierInvoiceExported: [false],
      customerInvoiceExported: [false],
      customerInvoiceDate: ['1970-01-01'],
      freightCostNotation: [''],
      versapayPaymentReference: [''],
      administrationNotation: [''],
      specialInvoice: [false]
    });

    this.claimInfoForm = this.formBuilder.group({
      loadDamageUuid: [''],
      customerName: [''],
      fbiReference: [''],
      dateNotified: ['1970-01-01'],
      dateSentToCarrier: ['1970-01-01'],
      dateSent: ['1970-01-01'],
      claimType: [''],
      claim: [''],
      claimCurrency: [''],
      amountPaidCarrier: [''],
      datePaidCarrier: ['1970-01-01'],
      amountPaidFbi: [''],
      datePaidFbi: ['1970-01-01'],
      paymentMethod: [''],
      reasonForDeclination: [''],
      loadDamageStatus: [''],
      damagedUnit: ['']
    });

    this.labourReviewForms = this.formBuilder.array([]);
    this.statusTimelineForms = this.formBuilder.array([]);
  }

  // Get subcontact form array for a specific contact type
  subcontactsFor(contactType: 'customer' | 'carrier' | 'consignee' | 'shipper'): FormArray {
    switch (contactType) {
      case 'customer': return this.customerInfoForm.get('subcontactInfo') as FormArray;
      case 'consignee': return this.consigneeInfoForm.get('subcontactInfo') as FormArray;
      case 'shipper': return this.shipperInfoForm.get('subcontactInfo') as FormArray;
      case 'carrier': return this.carrierInfoForm.get('subcontactInfo') as FormArray;
      default: throw new Error(`Invalid contact type: ${contactType}`);
    }
  }

  // Create a subcontact form group
  private createSubcontactFormGroup(data?: SubcontactInfo): FormGroup {
    return this.formBuilder.group({
      contactPhoneUuid: [data?.contact_phone_uuid || ''],
      contactUuid: [data?.contact_uuid || ''],
      phoneNumber: [data?.phone_number || ''],
      phonePrimary: [data?.phone_primary?.toString() || '0'],
      contactNameGiven: [data?.contact_name_given || ''],
      contactNameFamily: [data?.contact_name_family || ''],
      phoneDescription: [data?.phone_description || '']
    });
  }

  // Add a new subcontact
  addSubcontact(contactType: 'customer' | 'carrier' | 'consignee' | 'shipper'): void {
    const subcontacts = this.subcontactsFor(contactType);
    subcontacts.push(this.createSubcontactFormGroup());
  }

  // Remove a subcontact
  removeSubcontact(contactType: 'customer' | 'carrier' | 'consignee' | 'shipper', index: number): void {
    const subcontacts = this.subcontactsFor(contactType);
    const subcontact = subcontacts.at(index) as FormGroup;
    const contactPhoneUuid = subcontact.get('contactPhoneUuid')?.value;
    if (contactPhoneUuid) {
      // TODO: Implement API call to delete subcontact
      console.log(`Deleting subcontact with contact_phone_uuid: ${contactPhoneUuid}`);
    }
    subcontacts.removeAt(index);
    this.toastrService.info('Subcontact removed. Save to persist changes.');
  }

  // Load all data for the work order
  private async loadData(): Promise<void> {
    if (!this.ladingUuid) return;

    this.isLoading = true;
    //await this.fetchTimezones();

    this.loadBilling();
    const workOrderDetails$ = this.workOrderService.getWorkOrderDetails(this.ladingUuid);
    const documents$ = this.workOrderService.getDocuments(this.ladingUuid);
    const signatures$ = this.workOrderService.getSignatures(this.ladingUuid);
    const productImages$ = this.workOrderService.getProductImages(this.ladingUuid);
    console.log(productImages$); // this danged thing just prints the observable, doesn't it. i dunno?

// this is where you forked up
  forkJoin({
    workOrderResponse: workOrderDetails$,
    documents: documents$,
    signatures: signatures$,
    productImages: productImages$
  }).subscribe({
    next: ({ workOrderResponse, documents, signatures, productImages }) => {

      if (workOrderResponse.lading) {
        this.populateTimezones(workOrderResponse);
        this.populateGeneralInfo(workOrderResponse);
        this.populateNoteAudienceOptions(workOrderResponse);
        this.populateCarrierInfo(workOrderResponse);
        this.populateShipperInfo(workOrderResponse);
        this.populateConsigneeInfo(workOrderResponse);
        this.populateCustomerInfo(workOrderResponse);
        this.populateStatusTimeline(workOrderResponse);
        this.populateOperationInfo(workOrderResponse);
        this.populateLabourReviews(workOrderResponse);
        this.populateSalesInfo(workOrderResponse);
        this.populateAdministrationInfo(workOrderResponse);
        this.populateCustomerCareInfo(workOrderResponse);
        this.populateFreightInfo(workOrderResponse);
        this.ladingNotes = this.normalizeLadingNotes(workOrderResponse.lading_notes);
      }

      this.documentsTable = new MatTableDataSource(documents || []);
      this.productImagesTable = new MatTableDataSource(productImages?.images || []);
      this.signatures = signatures || [];

      this.isLoading = false;
      this.resetAllFormsPristine();
      if (this.statusTabActive) {
        this.loadStatusMapData();
      } else {
        this.statusMapLoaded = false;
        this.statusMapPoints = [];
        if (this.statusMap) {
          this.statusMap.remove();
          this.statusMap = null;
        }
      }
    },

    error: (error) => {
      console.error('Error loading data:', error);
      this.toastrService.error('Error loading work order data.');
      this.isLoading = false;
    }
  });
  }

  private normalizeLadingNotes(notes?: LadingNote[]): LadingNote[] {
    return (notes || []).map(note => ({
      ...note,
      contact_uuid: note.contact_uuid || ''
    }));
  }

  private populateNoteAudienceOptions(response: LadingResponse): void {
    const lading = response.lading;
    if (!lading) {
      this.noteAudienceOptions = [{ value: '', label: 'All Parties' }];
      this.defaultNoteContactUuid = '';
      return;
    }

    const seen = new Set<string>();
    const options: NoteAudienceOption[] = [{ value: '', label: 'All Parties' }];

    const pushOption = (value: string | null | undefined, label: string, fallback: string): void => {
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({
        value,
        label: `${label}: ${fallback || 'Unassigned'}`
      });
    };

    pushOption(lading.carrier_contact_uuid, 'Carrier', lading.carrier_name);
    pushOption(lading.customer_contact_uuid, 'Customer', lading.customer_name);
    pushOption(lading.shipper_contact_uuid, 'Shipper', lading.shipper_name);
    pushOption(lading.consignee_contact_uuid, 'Consignee', lading.consignee_name);

    this.noteAudienceOptions = options;
    this.defaultNoteContactUuid = lading.carrier_contact_uuid || '';
  }

  // Reset all forms to pristine state
  private resetAllFormsPristine(): void {
    this.generalInfoForm.markAsPristine();
    this.shipperInfoForm.markAsPristine();
    this.consigneeInfoForm.markAsPristine();
    this.customerInfoForm.markAsPristine();
    this.carrierInfoForm.markAsPristine();
    this.operationInfoForm.markAsPristine();
    this.customerCareInfoForm.markAsPristine();
    this.salesInfoForm.markAsPristine();
    this.administrationInfoForm.markAsPristine();
    this.claimInfoForm.markAsPristine();
    this.technicianInfoForm.markAsPristine();
    this.statusTimelineForms.controls.forEach(control => control.markAsPristine());
  }

  // Fetch installation form links
  private fetchInstallationFormLinks(): void {
    if (!this.ladingUuid) return;

    this.httpClient.get<any[]>(`/api/work_orders/installation_forms.php?uuid=${this.ladingUuid}`).subscribe({
      next: (response) => {
        if (response && Array.isArray(response)) {
          response.forEach(item => {
            const tokenParam = item.token ? `?token=${item.token}` : '';
            switch (item.token_type) {
              case 'webapp':
                this.installationFormLinks['inservice'] = `https://inservice.ulaap.com/${tokenParam}`;
                this.installationFormLinks['checklist'] = `https://checklist.ulaap.com/${tokenParam}`;
                this.installationFormLinks['disclaimer'] = `https://disclaimer.ulaap.com/${tokenParam}`;
                this.installationFormLinks['sitesurvey'] = `https://sitesurvey.ulaap.com/${tokenParam}`;
                break;
              case 'consignee':
                this.installationFormLinks['inagreement'] = `https://inagreement.ulaap.com/${tokenParam}`;
                break;
            }
          });
        }
      },
      error: (error) => console.error('Error fetching installation form links:', error)
    });
  }

  // Fetch timezones for shipper and consignee
  // private async fetchTimezones(): Promise<void> {
  //   if (!this.ladingUuid) return;

  //   try {
  //     const response = this.workOrderService.getWorkOrderDetails(this.ladingUuid);
  //     console.log('Consignee fetched info: ', response?.consignee)
  //     this.shipperTimezone = response?.shipper?.address?.timezone_id || 'America/Toronto';
  //     this.consigneeTimezone = response?.consignee?.address?.timezone_id || 'America/Toronto';
  //     this.generalInfoForm.get('appointmentTimezone')?.setValue(this.consigneeTimezone, { emitEvent: false });
  //   } catch (error) {
  //     console.error('Error fetching timezones:', error);
  //     this.shipperTimezone = 'America/Toronto';
  //     this.consigneeTimezone = 'America/Toronto';
  //     this.generalInfoForm.get('appointmentTimezone')?.setValue(this.consigneeTimezone, { emitEvent: false });
  //   }
  // }

  private normalizeDateInput(value: any): string | null {
    if (!value) return null;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
      return DateTime.fromJSDate(value).toFormat('yyyy-MM-dd');
    }
    try {
      return DateTime.fromISO(String(value)).toFormat('yyyy-MM-dd');
    } catch {
      return null;
    }
  }

  private normalizeWeightUnit(value: string | null | undefined): string {
    const normalized = (value || '').trim().toLowerCase();
    if (['lb', 'lbs', 'pound', 'pounds'].includes(normalized)) return 'lb';
    if (['kg', 'kgs', 'kilogram', 'kilograms'].includes(normalized)) return 'kg';
    return normalized;
  }

  private normalizeLengthUnit(value: string | null | undefined): string {
    const normalized = (value || '').trim().toLowerCase();
    if (['in', 'inch', 'inches'].includes(normalized)) return 'in';
    if (['ft', 'foot', 'feet'].includes(normalized)) return 'ft';
    if (['cm', 'centimeter', 'centimeters'].includes(normalized)) return 'cm';
    if (['m', 'meter', 'meters'].includes(normalized)) return 'm';
    return normalized;
  }

  // Populate form methods
  private isWhiteGloveLading(response: LadingResponse): boolean {
    const lading = response.lading as any;
    if (this.isWhiteGloveLadingType(lading?.lading_type)) return true;

    const ladingTypes = lading?.lading_types || (response as any).lading_types || [];
    return Array.isArray(ladingTypes) && ladingTypes.some(type =>
      this.isWhiteGloveLadingType(
        type?.lading_type
        ?? type?.lading_type_id
        ?? type?.type
        ?? type?.type_id
        ?? type
      )
    );
  }

  private isWhiteGloveLadingType(value: unknown): boolean {
    return String(value ?? '').trim() === '16';
  }

  private populateGeneralInfo(response: LadingResponse): void {
    if (!response.lading) return;

    const lading = response.lading;
    this.showWhiteGloveTab = this.isWhiteGloveLading(response);
    const [modifiedDate, modifiedTime] = lading.modified ? lading.modified.split(' ') : [null, null];

    this.alternate_reference = lading.alternate_reference;

    const displayPickupDate = this.shipperTimezone ? this.convertDbTimeToDisplay(lading.load_start, this.shipperTimezone) : { date: '', time: ''};
    const displayDeliveryDate = this.consigneeTimezone ? this.convertDbTimeToDisplay(lading.load_end, this.consigneeTimezone) : { date: '', time: ''}
    const displayAppointmentStart = lading.appointment_start && this.consigneeTimezone ? this.convertDbTimeToDisplay(lading.appointment_start, this.consigneeTimezone) : { date: '', time: ''} 
    const displayAppointmentEnd = lading.appointment_end && this.consigneeTimezone ? this.convertDbTimeToDisplay(lading.appointment_end, this.consigneeTimezone) : { date: '', time: ''}


    this.generalInfoForm.patchValue({
      ladingUuid: lading.lading_uuid,
      ladingNumber: lading.lading_number,
      proBill: lading.pro_bill,
      customerName: lading.customer_name,
      shipperName: lading.shipper_name,
      consigneeName: lading.consignee_name,
      carrierName: lading.carrier_name,
      ladingStatus: lading.lading_status,
      tripNumber: lading.trip_number,
      alternateReference: lading.alternate_reference,
      partialOrFull: lading.partial_or_full,
      ladingId: lading.lading_id,
      shipmentNumber: lading.shipment_number,
      thirdPartyTrackingUrl: lading.third_party_tracking_url || '',
      loadWeight: lading.load_weight?.toString() || '0',
      loadWeightActual: lading.load_weight_actual?.toString() || '0',
      loadWeightUnit: this.normalizeWeightUnit(lading.load_weight_unit),
      loadLength: lading.load_length?.toString() || '0',
      loadLengthUnit: this.normalizeLengthUnit(lading.load_length_unit),
      loadPieces: lading.load_pieces?.toString() || '0',
      loadPallets: lading.load_pallets?.toString() || '0',
      typeOfEquipment: lading.type_of_equipment,
      appointmentDate: displayAppointmentStart.date || '',
      appointmentStart: displayAppointmentStart.time,
      appointmentEnd: displayAppointmentEnd.time,
      appointmentTimezone: lading.appointment_timezone || this.consigneeTimezone,
      loadStartDate: displayPickupDate.date || '',
      loadStartTime: lading.load_start_time_known === false ? '' : displayPickupDate.time,
      loadEndDate: displayDeliveryDate.date || '',
      loadEndTime: lading.load_end_time_known === false ? '' : displayDeliveryDate.time,
      loadSpecialInfo: lading.load_special_info,
      carrierTrailer: lading.carrier_trailer,
      customerPo: lading.customer_po,
      weekNo: lading.week_no,
      created: lading.created ? lading.created.split(' ')[0] : '',
      modifiedDate: modifiedDate || '',
      modifiedTime: modifiedTime || '',
      estimatedLoadStart: lading.estimated_load_start ? lading.estimated_load_start.slice(0, 10) : '',
      estimatedLoadStartHours: lading.estimated_load_start_hours ?? '',
      estimatedLoadEnd: lading.estimated_load_end ? lading.estimated_load_end.slice(0, 10) : '',
      estimatedLoadEndHours: lading.estimated_load_end_hours ?? '',
      hot: lading.hot,
      expedited: lading.expedited,
      dropTrailer: lading.drop_trailer,
      dropTrailerStatus: lading.drop_trailer_status,
    }, { emitEvent: false }); 
  }

  private populateCarrierInfo(response: LadingResponse): void {
    if (!response.carrier) return;

    const carrier = response.carrier;
    const subcontacts = this.formBuilder.array(carrier.subcontacts?.map(s => this.createSubcontactFormGroup(s)) || []);
    this.carrierInfoForm.setControl('subcontactInfo', subcontacts);

    if (carrier.contact && carrier.contact.length > 0) {
      const contact = carrier.contact[0];
      const address = carrier.address?.[0];
      this.carrierInfoForm.patchValue({
        contactUuid: contact.contact_uuid,
        organization: contact.contact_organization,
        name: contact.contact_name_family ? `${contact.contact_name_given || ''} ${contact.contact_name_family}`.trim() : contact.contact_organization,
        streetAddress: address?.address_street || '',
        city: address?.address_locality || '',
        postalCode: address?.address_postal_code || '',
        country: address?.address_country || '',
        streetAddressExtended: address?.address_extended || ''
      }, { emitEvent: false });
    }
  }

  private populateContactRelatedForm(form: FormGroup, contactDetails: ContactDetails | undefined, contactType: string, mainContactName: string | null | undefined): void {
    if (!contactDetails) {
      form.reset({ contact_type: contactType, ladingUuid: this.ladingUuid });
      const nameToDisplay = mainContactName || contactType.charAt(0).toUpperCase() + contactType.slice(1).toLowerCase();
      if (contactType === 'SHIPPER') this.shipperAddressText = `${nameToDisplay} - Details not available.`;
      if (contactType === 'CONSIGNEE') this.consigneeAddressText = `${nameToDisplay} - Details not available.`;
      return;
    }

    const subcontacts = contactDetails.subcontacts?.map(s => this.createSubcontactFormGroup(s)) || [];
    form.setControl('subcontactInfo', this.formBuilder.array(subcontacts));

    const nameToUse = contactDetails.contact?.[0]?.contact_organization || mainContactName || contactType;
    if (contactDetails.address && contactDetails.address.length > 0) {
      if (contactType === 'SHIPPER') this.shipperAddressText = this.formatAddressText(contactDetails.address, nameToUse);
      if (contactType === 'CONSIGNEE') this.consigneeAddressText = this.formatAddressText(contactDetails.address, nameToUse);
      if (contactType === 'CUSTOMER') this.customerAddressText = this.formatAddressText(contactDetails.address, nameToUse)
    } else {
      if (contactType === 'SHIPPER') this.shipperAddressText = `${nameToUse} - Address not available.`;
      if (contactType === 'CONSIGNEE') this.consigneeAddressText = `${nameToUse} - Address not available.`;
    }

    const contact = contactDetails.contact?.[0];
    const siteChecks = contactDetails.site_checks?.[0];
    let patchData: any = { ladingUuid: this.ladingUuid, contactType };

    if (contact) {
      patchData.contactUuid = contact.contact_uuid;
      if (contactType === 'SHIPPER') {
        Object.assign(patchData, {
          domainUuid: contact.domain_uuid,
          contactOrganization: contact.contact_organization,
          contactNameGiven: contact.contact_name_given,
          contactNameMiddle: contact.contact_name_middle,
          contactNameFamily: contact.contact_name_family,
          contactEmail: contact.contact_email,
          usDotNo: contact.us_dot_no,
          active: contact.active,
          carrierMc: contact.carrier_mc,
          poolId: contact.pool_id,
          routeCode: contact.route_code,
          dropTrailerParty: contact.drop_trailer_party
        });
      }
    }

    if (siteChecks) {
      const siteCheckBooleans: { [key: string]: boolean } = {};
      Object.keys(siteChecks).forEach(key => {
        const value = siteChecks[key as keyof SiteCheckInfo];
        if (typeof value === 'string') {
          siteCheckBooleans[key] = value.toLowerCase() === 't';
        } else if (typeof value === 'boolean') {
          siteCheckBooleans[key] = value;
        }
      });
      patchData = { ...patchData, ...siteChecks, ...siteCheckBooleans };
    }

    form.patchValue(patchData, { emitEvent: false });
  }

  private populateTimezones(response: LadingResponse): void {
    if (response.shipper.address && response.shipper.address.length > 0){
      this.shipperTimezone = response.shipper.address[0].timezone_id || 'America/Toronto';
    }
    else{
      this.shipperTimezone = 'America/Toronto';
    }
    if (response.consignee.address && response.consignee.address.length > 0){
      this.consigneeTimezone = response.consignee.address[0].timezone_id || 'America/Toronto';
      this.generalInfoForm.get('appointmentTimezone')?.setValue(this.consigneeTimezone, { emitEvent: false });
    }
    else{
      this.consigneeTimezone = 'America/Toronto';
      this.generalInfoForm.get('appointmentTimezone')?.setValue(this.consigneeTimezone, { emitEvent: false });
    }

    [this.shipperTimezone, this.consigneeTimezone].forEach(timeZoneId => {
      if (timeZoneId && !this.timezones.includes(timeZoneId)) {
        this.timezones.push(timeZoneId);
      }
    });
  }

  private populateShipperInfo(response: LadingResponse): void {
    this.populateContactRelatedForm(this.shipperInfoForm, response.shipper, 'SHIPPER', response.lading?.shipper_name);
  }

  private populateConsigneeInfo(response: LadingResponse): void {
    this.populateContactRelatedForm(this.consigneeInfoForm, response.consignee, 'CONSIGNEE', response.lading?.consignee_name);
  }

  private populateCustomerInfo(response: LadingResponse): void {
    this.populateContactRelatedForm(this.customerInfoForm, response.customer, 'CUSTOMER', response.lading?.customer_name);
  }

  private populateOperationInfo(response: LadingResponse): void {
    const operationInfo = response.lading?.operations?.[0] as any;
    this.operationInfoForm.reset({ ladingUuid: this.ladingUuid, ladingOperationUuid: operationInfo?.lading_operation_uuid || operationInfo?.ladingOperationUuid || null });
    if (operationInfo) {
      this.operationInfoForm.patchValue(this.toOperationFormValue(operationInfo), { emitEvent: false });
    }
  }

  private toOperationFormValue(operationInfo: any): { [key: string]: any } {
    return {
      ladingUuid: operationInfo.ladingUuid ?? operationInfo.lading_uuid ?? this.ladingUuid,
      ladingOperationUuid: operationInfo.ladingOperationUuid ?? operationInfo.lading_operation_uuid ?? null,
      carrierRated: this.toBoolean(operationInfo.carrierRated ?? operationInfo.carrier_rated, false),
      carrierRerated: this.toBoolean(operationInfo.carrierRerated ?? operationInfo.carrier_rerated, false),
      strapsBars: this.toBoolean(operationInfo.strapsBars ?? operationInfo.straps_bars, false),
      liftLull: this.toBoolean(operationInfo.liftLull ?? operationInfo.lift_lull, false),
      callSite: this.toBoolean(operationInfo.callSite ?? operationInfo.call_site, false),
      protectiveEquipment: this.toBoolean(operationInfo.protectiveEquipment ?? operationInfo.protective_equipment, false),
      unmannedJobSite: this.toBoolean(operationInfo.unmannedJobSite ?? operationInfo.unmanned_job_site, false),
      operationsNotation: operationInfo.operationsNotation ?? operationInfo.operations_notation ?? '',
      loadedOdot: this.toBoolean(operationInfo.loadedOdot ?? operationInfo.loaded_odot, false),
      loadingDelay: this.toBoolean(operationInfo.loadingDelay ?? operationInfo.loading_delay, false),
      documentDelay: this.toBoolean(operationInfo.documentDelay ?? operationInfo.document_delay, false),
      borderDelay: this.toBoolean(operationInfo.borderDelay ?? operationInfo.border_delay, false),
      customsInspection: this.toBoolean(operationInfo.customsInspection ?? operationInfo.customs_inspection, false),
      trailerUnavailable: this.toBoolean(operationInfo.trailerUnavailable ?? operationInfo.trailer_unavailable, false),
      driverUnavailable: this.toBoolean(operationInfo.driverUnavailable ?? operationInfo.driver_unavailable, false),
      driverHos: this.toBoolean(operationInfo.driverHos ?? operationInfo.driver_hos, false),
      carrierReneged: this.toBoolean(operationInfo.carrierReneged ?? operationInfo.carrier_reneged, false),
      mechanicalDelay: this.toBoolean(operationInfo.mechanicalDelay ?? operationInfo.mechanical_delay, false),
      trailerSwitch: this.toBoolean(operationInfo.trailerSwitch ?? operationInfo.trailer_switch, false),
      weatherDelay: this.toBoolean(operationInfo.weatherDelay ?? operationInfo.weather_delay, false),
      dockWaiting: this.toBoolean(operationInfo.dockWaiting ?? operationInfo.dock_waiting, false),
      dockReDelivery: this.toBoolean(operationInfo.dockReDelivery ?? operationInfo.dock_re_delivery, false),
      scheduledHoliday: this.toBoolean(operationInfo.scheduledHoliday ?? operationInfo.scheduled_holiday, false)
    };
  }

  private populateLabourReviews(response: LadingResponse): void {
    const reviews = response.lading?.lading_labour_reviews || response.lading_labour_reviews || [];
    this.ladingLabourReviews = reviews;
    this.labourReviewForms.clear();
    reviews.forEach(review => this.labourReviewForms.push(this.createLabourReviewApprovalForm(review)));
  }

  private createLabourReviewApprovalForm(review: LadingLabourReview): FormGroup {
    return this.formBuilder.group({
      approvedInstallMinutes: [
        this.pickLabourNumber(review.approved_install_minutes, review.requested_install_minutes, review.estimated_install_minutes, 0),
        [Validators.min(0)]
      ],
      approvedHandlingMinutes: [
        this.pickLabourNumber(review.approved_handling_minutes, review.requested_handling_minutes, review.estimated_handling_minutes, 0),
        [Validators.min(0)]
      ],
      approvedPeople: [
        this.pickLabourWholeNumber(review.approved_people, review.requested_people, review.estimated_people),
        [Validators.min(0)]
      ],
      approvedNotes: [review.approved_notes || ''],
      rerateRequested: [this.toBoolean(review.rerate_requested, true)]
    });
  }

  private populateSalesInfo(response: LadingResponse): void {
    const salesInfo = response.lading?.sales?.[0];
    this.salesInfoForm.reset({ ladingUuid: this.ladingUuid, ladingSaleUuid: salesInfo?.lading_sale_uuid || null });
    if (salesInfo) {
      this.salesInfoForm.patchValue(salesInfo, { emitEvent: false });
    }
  }

  private populateCustomerCareInfo(response: LadingResponse): void {
    const careInfo = response.lading?.cares?.[0];
    this.customerCareInfoForm.reset({ ladingUuid: this.ladingUuid, ladingCareUuid: careInfo?.lading_care_uuid || null });
    if (careInfo) {
      this.customerCareInfoForm.patchValue(careInfo, { emitEvent: false });
    }
  }

  private populateAdministrationInfo(response: LadingResponse): void {
    const adminInfo = response.lading?.administrations?.[0];
    this.administrationInfoForm.reset({ ladingUuid: this.ladingUuid, ladingAdministrationUuid: adminInfo?.lading_administration_uuid || null });
    if (adminInfo) {
      const patchData = { ...adminInfo };
      if (patchData.customer_invoice_date === '0000-00-00' || !patchData.customer_invoice_date) {
        patchData.customer_invoice_date = null;
      } else if (patchData.customer_invoice_date) {
        patchData.customer_invoice_date = patchData.customer_invoice_date.slice(0, 10);
      }
      this.administrationInfoForm.patchValue(patchData, { emitEvent: false });
    }
  }

  private populateStatusTimeline(response: LadingResponse): void {
    const timeline = (response.lading.status_timeline || []) as StatusTimelineInfo[];
    this.originalStatusValues = [];
    const currentStatus = response.lading.lading_status;
    const currentUpdated = this.statusTimestampKey(response.lading.lading_status_updated);

    const statusForms: FormGroup[] = timeline.map(status => {
      const localDate = status.local_time ? status.local_time.slice(0, 10) : null;
      const localTime = status.local_time ? status.local_time.slice(11, 16) : null;
      const statusNote = status.lading_status_note ?? null;
      const localTimeZone = status.time_zone_id || this.consigneeTimezone || 'America/Toronto';
      const canonicalEvent = status.lading_status_updated
        ? DateTime.fromSQL(status.lading_status_updated, { zone: 'America/New_York' })
        : null;
      const fallbackEvent = localDate && localTime
        ? DateTime.fromISO(`${localDate}T${localTime}`, { zone: localTimeZone })
        : null;
      const eventTime = canonicalEvent?.isValid
        ? canonicalEvent.setZone(this.browserTimezone)
        : fallbackEvent?.isValid
          ? fallbackEvent.setZone(this.browserTimezone)
          : null;

      if (!this.timezones.includes(localTimeZone)) {
        this.timezones.push(localTimeZone);
      }
      if (status.lading_status && !this.ladingStatuses.includes(status.lading_status)) {
        this.ladingStatuses.push(status.lading_status);
      }

      const formValue: StatusTimelineFormValue = {
        status: status.lading_status,
        createdAt: status.created || null,
        modifiedAt: status.modified_time || null,
        ladingUuid: this.ladingUuid,
        timelineUuid: status.lading_status_timeline_uuid,
        eventDate: eventTime?.toFormat('yyyy-MM-dd') || null,
        eventTime: eventTime?.toFormat('HH:mm') || null,
        eventTimeZone: this.browserTimezone,
        dateLocal: localDate,
        timeLocal: localTime,
        localTimeZone,
        localOverride: false,
        note: statusNote,
        isCurrent: status.lading_status === currentStatus
          && this.statusTimestampKey(status.lading_status_updated) === currentUpdated
      };

      this.originalStatusValues.push(formValue);
      return this.createStatusTimelineForm(formValue);
    });

    this.statusTimelineForms = this.formBuilder.array(statusForms);
  }

  private createStatusTimelineForm(value: StatusTimelineFormValue): FormGroup {
    const form = this.formBuilder.group({
      status: [value.status, Validators.required],
      createdAt: [{ value: value.createdAt, disabled: true }],
      modifiedAt: [{ value: value.modifiedAt, disabled: true }],
      eventDate: [value.eventDate, Validators.required],
      eventTime: [value.eventTime, [
        Validators.required,
        Validators.pattern('^([01]\\d|2[0-3]):([0-5]\\d)$')
      ]],
      eventTimeZone: [value.eventTimeZone, Validators.required],
      dateLocal: [value.dateLocal, Validators.required],
      timeLocal: [value.timeLocal, [
        Validators.required,
        Validators.pattern('^([01]\\d|2[0-3]):([0-5]\\d)$')
      ]],
      localTimeZone: [{ value: value.localTimeZone, disabled: true }, Validators.required],
      localOverride: [value.localOverride],
      ladingUuid: [value.ladingUuid],
      timelineUuid: [value.timelineUuid],
      note: [value.note],
      isCurrent: [value.isCurrent]
    });

    this.setLocalTimeControlsState(form, value.localOverride);
    ['eventDate', 'eventTime', 'eventTimeZone'].forEach(controlName => {
      form.get(controlName)?.valueChanges.subscribe(() => {
        if (form.get('localOverride')?.value !== true) {
          this.syncLocalTimePreview(form);
        }
      });
    });

    return form;
  }

  toggleLocalTimeOverride(statusForm: FormGroup): void {
    const enableOverride = statusForm.get('localOverride')?.value !== true;
    statusForm.get('localOverride')?.setValue(enableOverride, { emitEvent: false });

    if (!enableOverride) {
      this.syncLocalTimePreview(statusForm);
    }
    this.setLocalTimeControlsState(statusForm, enableOverride);
    statusForm.markAsDirty();
  }

  private setLocalTimeControlsState(statusForm: FormGroup, overrideEnabled: boolean): void {
    ['dateLocal', 'timeLocal'].forEach(controlName => {
      const control = statusForm.get(controlName);
      if (overrideEnabled) {
        control?.enable({ emitEvent: false });
      } else {
        control?.disable({ emitEvent: false });
      }
    });
  }

  private syncLocalTimePreview(statusForm: FormGroup): void {
    const eventDate = statusForm.get('eventDate')?.value;
    const eventTime = statusForm.get('eventTime')?.value;
    const eventTimeZone = statusForm.get('eventTimeZone')?.value;
    const localTimeZone = statusForm.get('localTimeZone')?.value;
    if (!eventDate || !eventTime || !eventTimeZone || !localTimeZone) return;

    const submittedTime = DateTime.fromISO(`${eventDate}T${eventTime}`, { zone: eventTimeZone });
    if (!submittedTime.isValid) return;

    const localTime = submittedTime.setZone(localTimeZone);
    statusForm.patchValue({
      dateLocal: localTime.toFormat('yyyy-MM-dd'),
      timeLocal: localTime.toFormat('HH:mm')
    }, { emitEvent: false });
  }

  private statusTimestampKey(value: string | null | undefined): string {
    return value ? value.slice(0, 19) : '';
  }

  private populateFreightInfo(response: LadingResponse): void {
    this.packages = response.lading?.packages || [];
    this.products = response.lading?.products || [];
    this.deliveryRequirements = response.tg_delivery_reqs || [];
  }

  // Group delivery requirements for display
  get groupedDeliveryRequirements(): { item_code: string; item_description: string; sales_order_number?: string; quantity: number }[] {
    const map = this.deliveryRequirements.reduce((acc, req) => {
      if (!req.item_code) return acc;

      let itemDescription = 'N/A';
      const allProducts = [...this.products, ...this.packages.flatMap(pkg => pkg.products || [])];
      const product = allProducts.find(prod => prod.product_code === req.item_code);
      if (product && product.description) itemDescription = product.description;

      if (!acc[req.item_code]) {
        acc[req.item_code] = {
          item_code: req.item_code,
          item_description: itemDescription,
          sales_order_number: req.sales_order_number,
          quantity: 0
        };
      }

      acc[req.item_code].quantity += Number(req.quantity) || 0;
      if (!acc[req.item_code].sales_order_number && req.sales_order_number) {
        acc[req.item_code].sales_order_number = req.sales_order_number;
      }

      return acc;
    }, {} as Record<string, { item_code: string; item_description: string; sales_order_number?: string; quantity: number }>);

    return Object.values(map);
  }

  // Compare products and delivery requirements
  // private compareProductsAndDeliveryRequirements(): boolean {
  //   const actualQuantities = new Map<string, number>();
  //   this.packages.forEach(pkg => {
  //     pkg.products?.forEach(prod => {
  //       if (prod.product_code) {
  //         actualQuantities.set(prod.product_code, (actualQuantities.get(prod.product_code) || 0) + 1);
  //       }
  //     });
  //   });
  //   this.products.forEach(prod => {
  //     if (prod.product_code) {
  //       actualQuantities.set(prod.product_code, (actualQuantities.get(prod.product_code) || 0) + 1);
  //     }
  //   });

  //   const expectedQuantities = new Map<string, number>();
  //   this.deliveryRequirements.forEach(req => {
  //     if (req.item_code && req.quantity != null) {
  //       expectedQuantities.set(req.item_code, (expectedQuantities.get(req.item_code) || 0) + Number(req.quantity));
  //     }
  //   });

  //   for (const [itemCode, expectedQty] of expectedQuantities) {
  //     const actualQty = actualQuantities.get(itemCode) || 0;
  //     if (actualQty !== expectedQty) {
  //       console.warn(`Discrepancy: Item ${itemCode} - Expected: ${expectedQty}, Actual: ${actualQty}`);
  //       return false;
  //     }
  //   }

  //   for (const [itemCode, actualQty] of actualQuantities) {
  //     if (!expectedQuantities.has(itemCode)) {
  //       console.warn(`Discrepancy: Actual item ${itemCode} (Qty: ${actualQty}) not expected.`);
  //       return false;
  //     }
  //   }

  //   return true;
  // }

  // Freight actions
  onAddPackage(): void {
    this.toastrService.info('Add Package: TODO - Implement dialog and service call.');
  }

  onEditPackage(packageInfo: PackageInfo): void {
    console.log('Edit Package:', packageInfo);
    // TODO: Implement edit package logic
  }

  onDeletePackage(packageInfo: PackageInfo): void {
    console.log('Delete Package:', packageInfo);
    // TODO: Implement delete package logic
  }

  onPackageAction(packageUuid: string): void {
    console.log('Package action:', packageUuid);
    // TODO: Implement package action logic
  }

  onAddProduct(packageUuid?: string): void {
    console.log('Add Product to:', packageUuid || 'lading-level');
    // TODO: Implement add product logic
  }

  onEditProduct(product: ProductInfo, packageUuid?: string): void {
    console.log('Edit Product:', product, 'in package:', packageUuid);
    // TODO: Implement edit product logic
  }

  onDeleteProduct(product: ProductInfo, packageUuid?: string): void {
    console.log('Delete Product:', product, 'in package:', packageUuid);
    // TODO: Implement delete product logic
  }

  onProductAction(productUuid: string, packageUuid?: string): void {
    console.log('Product action:', productUuid, 'in package:', packageUuid);
    // TODO: Implement product action logic
  }

  onAddDeliveryRequirement(): void {
    console.log('Add Delivery Requirement');
    // TODO: Implement add delivery requirement logic
  }

  onEditDeliveryRequirement(req: any): void {
    console.log('Edit Delivery Requirement:', req);
    // TODO: Implement edit delivery requirement logic
  }

  onDeleteDeliveryRequirement(req: any): void {
    console.log('Delete Delivery Requirement:', req);
    // TODO: Implement delete delivery requirement logic
  }

  onDeliveryRequirementAction(itemCodeOrUuid: string): void {
    console.log('Delivery action:', itemCodeOrUuid);
    // TODO: Implement delivery requirement action logic
  }

  /*
  July 4, 2025
  drevil1950 — 11:28 AM
    Hi Jennifer
    can you find Byron's new fright page code
    and make it the freight page for newams2
    do you know where he hid this component without asking him
  j_do.ai — 11:33 AM
    i can link to it, but would byron be okay with it? i don't need the sr dev getting mad at me for going behind his back 😅
  drevil1950 - 11:33 AM
    [absolutely_im_the_boss.gif]
  */
  onFreightPageClick(): void {
    window.open(`/freight/${this.ladingUuid}`, '_blank');
  }

  getThirdPartyTrackingUrl(): string | null {
    const rawUrl = this.generalInfoForm?.get('thirdPartyTrackingUrl')?.value;
    const trackingUrl = String(rawUrl ?? '').trim();

    if (!trackingUrl) return null;
    if (/^https?:\/\//i.test(trackingUrl)) return trackingUrl;
    if (/^[a-z][a-z\d+\-.]*:/i.test(trackingUrl)) return null;

    return `https://${trackingUrl}`;
  }

  // private refreshFreightInfo(): void {
  //   if (!this.ladingUuid) return;

  //   this.isLoading = true;
  //   this.workOrderService.getWorkOrderDetails(this.ladingUuid).subscribe({
  //     next: (response) => {
  //       if (response.lading) {
  //         this.populateFreightInfo(response);
  //         this.hasExpectedProducts = this.compareProductsAndDeliveryRequirements();
  //       }
  //       this.isLoading = false;
  //     },
  //     error: (error) => {
  //       console.error('Error refreshing freight info:', error);
  //       this.toastrService.error('Could not refresh freight details.');
  //       this.isLoading = false;
  //     }
  //   });
  // }

  // Document handling
  handleDocumentDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOverDocument = true;
  }

  handleDocumentDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOverDocument = false;
  }

  handleDocumentDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOverDocument = false;

    const files = event.dataTransfer?.files;
    if (!files?.length) return;

    if (files.length > 1) {
      this.toastrService.info('Upload one document at a time so each file gets the correct document type.');
    }

    const file = files.item(0);
    if (file) this.prepareDocumentUpload(file);
  }

  handleDocumentFileSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);
    if (file) this.prepareDocumentUpload(file);
    input.value = '';
  }

  private prepareDocumentUpload(file: File): void {
    if (!this.ladingUuid) return;

    const documentFormData = new FormData();
    documentFormData.append('file', file, file.name);
    documentFormData.append('lading_uuid', this.ladingUuid);

    const altRef =
      this.generalInfoForm.get('alternateReference')?.value ||
      this.alternate_reference;
    if (altRef) {
      documentFormData.append('alternate_reference', altRef);
    }

    this.openDocumentTypeDialog(documentFormData);
  }

  private openDocumentTypeDialog(documentFormData: FormData): void {
    const dialogRef = this.dialog.open(DocumentTypeSelectionDialog2, {
      width: '400px',
    });
    dialogRef.afterClosed().subscribe((result) => {
      console.log(`Received result ${JSON.stringify(result)} from dialog`);
      if (result && result.value) {
        documentFormData.append('document_type', result.value.toString());
        documentFormData.append('file_description', result.name);
        this.uploadDocument(documentFormData);
      }
    });
  }

  private uploadDocument(documentFormData: FormData): void {
    this.workOrderService.uploadDocuments(documentFormData).subscribe({
      next: (response: any) => {
        if (response.success && this.ladingUuid) {
          this.toastrService.success('File uploaded.');
          this.loadDocuments();
        } else {
          this.toastrService.error(`File upload failed: ${response.message}`);
        }
      },
      error: (error) => {
        console.error('Upload error:', error);
        this.toastrService.error('Error uploading file.');
      },
    });
  }
  
  private loadDocuments(): void {
    if (!this.ladingUuid) return;

    this.workOrderService.getDocuments(this.ladingUuid).subscribe({
      next: (documents) => {
        this.documentsTable = new MatTableDataSource(documents || []);
      },
      error: (error) => console.error('Error loading documents:', error)
    });
  }

  getDocumentTypeLabel(documentType: number | null): string {
    const type = Number(documentType);
    return this.documentTypeLabels[type] || (documentType ? `Type ${documentType}` : 'Uncategorized');
  }

  getDocumentIconClass(fileFormat: string | null): string {
    const format = (fileFormat || '').toLowerCase();
    if (format === 'pdf') return 'fas fa-file-pdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'tif', 'tiff'].includes(format)) {
      return 'fas fa-file-image';
    }
    return 'fas fa-file-alt';
  }

  formatFileSize(value: string | number | null): string {
    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes <= 0) return 'Size unavailable';
    if (bytes < 1024) return `${bytes} B`;

    const units = ['KB', 'MB', 'GB'];
    let size = bytes / 1024;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size >= 10 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
  }

  private triggerDocumentDownload(fileUrl: string, fileName: string): void {
    const link = window.document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
  }

  async downloadDocument(document: DocumentInfo): Promise<void> {
    this.downloadingDocumentUuid = document.upload_document_uuid;
    try {
      const fileUrl = await this.workOrderService.getDocumentFileURL(document);
      this.triggerDocumentDownload(fileUrl, document.file_name);
    } catch (error) {
      console.error('Error downloading document:', error);
      this.toastrService.error('Could not download document.');
    } finally {
      this.downloadingDocumentUuid = null;
    }
  }

  async viewSignature(signature: SignatureInfo): Promise<void> {
    if (!signature.signature_uuid) return;

    try {
      const fileUrl = await this.workOrderService.getSignatureFileURL(signature);

      this.dialog.open(DocumentModalComponent, { 
        width: '90vw', 
        data: { 
          fileURL: fileUrl,
          type: 'signature',
          isImage: true,
          signer: signature.signatory 
        } 
      });

    } catch (error) {
      console.error('Error fetching signature:', error);
      this.toastrService.error('Could not load signature.');
    }
  }

  async openDocument(document: DocumentInfo): Promise<void> {
    const format = (document.file_format || '').toLowerCase();

    this.openingDocumentUuid = document.upload_document_uuid;
    try {
      const fileUrl = await this.workOrderService.getDocumentFileURL(document);

      if (format === 'pdf') {
        this.dialog.open(DocumentModalComponent, {
          width: '92vw',
          maxWidth: '1100px',
          data: {
            fileURL: fileUrl,
            fileName: document.file_name,
            isPdf: true,
            type: 'document'
          }
        });
        return;
      }

      if (format === 'tif' || format === 'tiff') {
        this.triggerDocumentDownload(fileUrl, document.file_name);
        return;
      }

      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(format)) {
        this.dialog.open(DocumentModalComponent, {
          width: '90vw',
          data: {
            fileURL: fileUrl,
            isImage: true,
            type: 'document'
          }
        });
        return;
      }

      this.triggerDocumentDownload(fileUrl, document.file_name);
    } catch (error) {
      console.error('Error opening document:', error);
      this.toastrService.error('Could not open document.');
    } finally {
      this.openingDocumentUuid = null;
    }
  }

  // Form submission methods
  submitGeneralInfoForm(): void {
    if (!this.generalInfoForm.valid) {
      this.toastrService.error('Correct errors in General Info.');
      this.generalInfoForm.markAllAsTouched();
      return;
    }

    const statusControl = this.generalInfoForm.get('ladingStatus');
    const shouldUpdateLadingStatus = !!statusControl?.dirty;
    const postData = this.buildGeneralInfoPostData();
    console.log(postData);
    const hasGeneralInfoChanges = Object.keys(postData).some(key => key !== 'lading_uuid');

    if (!hasGeneralInfoChanges && !shouldUpdateLadingStatus) {
      this.toastrService.info('No changes to save in General Info.');
      return;
    }

    if (hasGeneralInfoChanges) {
      this.httpClient.post<any>(`/api/work_orders/work_orders_grid_details.php?uuid=${this.ladingUuid}`, postData).subscribe({
        next: (response) => {
          console.log(response);
          this.toastrService.success('General info saved.');
          this.markGeneralInfoControlsPristine(shouldUpdateLadingStatus ? ['ladingStatus'] : []);
        },
        error: (error) => {
          console.error('Error saving general info:', error);
          this.toastrService.error('Error saving general information.');
        }
      });
    }

    if (shouldUpdateLadingStatus) {
      this.submitLadingStatusUpdate(statusControl?.value);
    }
  }

  private buildGeneralInfoPostData(): { [key: string]: any } {
    const postData: { [key: string]: any } = {};
    Object.keys(this.generalInfoForm.controls).forEach(controlName => {
      if (controlName === 'ladingStatus') return;

      const control = this.generalInfoForm.get(controlName);
      if (control && (control.dirty || controlName === 'ladingUuid')) {
        let value = control.value;
        const key = controlName.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        if (typeof value === 'boolean') postData[controlName] = value ? 't' : 'f';
        else if (value === null || value === undefined) postData[key] = null;
        else postData[key] = value;
      }
    });

    const conversions = [
      { dateField: 'loadStartDate', timeField: 'loadStartTime', postDateField: 'load_start_date', postTimeField: 'load_start_time', targetField: 'load_start', timezone: this.shipperTimezone },
      { dateField: 'loadEndDate', timeField: 'loadEndTime', postDateField: 'load_end_date', postTimeField: 'load_end_time', targetField: 'load_end', timezone: this.consigneeTimezone }
    ];

    conversions.forEach(conv => {
      const dateControl = this.generalInfoForm.get(conv.dateField!);
      const timeControl = this.generalInfoForm.get(conv.timeField!);
      if (dateControl?.dirty || timeControl?.dirty || postData.hasOwnProperty(conv.dateField!) || postData.hasOwnProperty(conv.timeField!)) {
        const dateStr = this.normalizeDateInput(dateControl?.value);
        const timeStr = timeControl?.value || '00:00';
        if (dateStr && conv.timezone) {
          postData[conv.targetField] = this.convertDisplayToDbTime(dateStr, timeStr, conv.timezone);
        } else {
          postData[conv.targetField] = null;
        }
        delete postData[conv.postDateField!];
        delete postData[conv.postTimeField!];
      }
    });

    const appointmentDateControl = this.generalInfoForm.get('appointmentDate');
    const appointmentStartControl = this.generalInfoForm.get('appointmentStart');
    const appointmentEndControl = this.generalInfoForm.get('appointmentEnd');
    const appointmentTimezone = this.generalInfoForm.get('appointmentTimezone')?.value || this.consigneeTimezone;

    if (appointmentDateControl?.dirty || appointmentStartControl?.dirty || appointmentEndControl?.dirty) {
      const dateStr = this.normalizeDateInput(appointmentDateControl?.value);
      if (dateStr) {
        try {
          postData['appointment_date'] = DateTime.fromISO(dateStr).toFormat('yyyy-MM-dd') + ' 00:00:00';
        } catch (e) {
          postData['appointment_date'] = null;
        }
        const startStr = appointmentStartControl?.value;
        if (startStr) {
          postData['appointment_start'] = this.convertDisplayToDbTime(dateStr, startStr, appointmentTimezone);
        } else if (appointmentStartControl?.dirty) {
          postData['appointment_start'] = null;
        }
        const endStr = appointmentEndControl?.value;
        if (endStr) {
          postData['appointment_end'] = this.convertDisplayToDbTime(dateStr, endStr, appointmentTimezone);
        } else if (appointmentEndControl?.dirty) {
          postData['appointment_end'] = null;
        }
      } else {
        postData['appointment_date'] = null;
        postData['appointment_start'] = null;
        postData['appointment_end'] = null;
      }
    }

    return postData;
  }

  private submitLadingStatusUpdate(status: string | null | undefined): void {
    if (!this.ladingUuid) {
      this.toastrService.error('Lading UUID missing.');
      return;
    }

    if (!status) {
      this.toastrService.warning('No status selected.', 'Status Update');
      return;
    }

    const postData = {
      lading_uuid: this.ladingUuid,
      lading_status: status
    };

    this.httpClient.post<any>('/api/lading_status/add_lading_status.php', postData).subscribe({
      next: (response) => {
        console.log('Lading status updated:', response);
        this.toastrService.success(`Status updated to "${status}" successfully.`, 'Lading Status Updated');
        this.generalInfoForm.get('ladingStatus')?.markAsPristine();
        this.refreshStatusMap();
      },
      error: (error) => {
        console.error('Lading status not updated:', error);
        this.toastrService.error('Failed to update lading status.');
      }
    });
  }

  private markGeneralInfoControlsPristine(skipControlNames: string[] = []): void {
    Object.keys(this.generalInfoForm.controls).forEach(controlName => {
      if (!skipControlNames.includes(controlName)) {
        this.generalInfoForm.get(controlName)?.markAsPristine();
      }
    });
  }

  saveNotes(): void {
    if (!this.ladingUuid) {
      this.toastrService.error('Lading UUID missing.');
      return;
    }

    const postData = {
      lading_uuid: this.ladingUuid,
      notes: this.ladingNotes.map(note => ({
        note: note.note,
        lading_note_uuid: note.lading_note_uuid || undefined,
        contact_uuid: note.contact_uuid || undefined
      }))
    };

    this.httpClient.post<any>('/api/work_orders/details/process_lading_notes.php', postData).subscribe({
      next: (response) => {
        if (response?.success) {
          this.ladingNotes = this.normalizeLadingNotes(response.lading_notes);
          this.toastrService.success('Notes saved.');
        } else {
          this.toastrService.error(response.message || 'Error saving notes.');
        }
      },
      error: (error) => {
        console.error('Error saving notes:', error);
        this.toastrService.error('Error saving notes.');
      }
    });
  }

  postSiteChecks(form: FormGroup, contactType: string): void {
    let hasNonContactChanges = false;
    Object.keys(form.controls).forEach(controlName => {
      if (controlName !== 'subcontactInfo' && controlName !== 'contactUuid' && form.get(controlName)?.dirty) {
        hasNonContactChanges = true;
      }
    });

    if (!hasNonContactChanges && !form.get('ladingSiteCheckUuid')?.value) {
      this.toastrService.info(`No changes to save for ${contactType} site checks.`);
      return;
    }

    if (!this.ladingUuid) {
      this.toastrService.error('Lading UUID missing.');
      return;
    }

    const postData: { [key: string]: any } = {};
    const alwaysSend = ['ladingUuid', 'contactType', 'contactUuid', 'ladingSiteCheckUuid'];

    Object.keys(form.controls).forEach(controlName => {
      if (controlName !== 'subcontactInfo') {
        const control = form.get(controlName);
        if (control && (control.dirty || (alwaysSend.includes(controlName) && control.value))) {
          let value = control.value;
          if (typeof value === 'boolean') postData[controlName] = value ? 't' : 'f';
          else if (value === '' && !alwaysSend.includes(controlName)) postData[controlName] = null;
          else if (value !== null && value !== undefined) postData[controlName] = value;
        }
      }
    });

    if (!postData['ladingSiteCheckUuid']) {
      postData['contactType'] = form.get('contactType')?.value;
      postData['contactUuid'] = form.get('contactUuid')?.value;
      postData['ladingUuid'] = this.ladingUuid;
      delete postData['ladingSiteCheckUuid'];
    } else {
      delete postData['ladingUuid'];
      delete postData['contactType'];
      delete postData['contactUuid'];
    }

    const excludedFields = [
      'domainUuid', 'contactOrganization', 'contactNameGiven', 'contactNameMiddle',
      'contactNameFamily', 'contactEmail', 'usDotNo', 'active', 'carrierMc', 'poolId',
      'routeCode', 'dropTrailerParty'
    ];
    excludedFields.forEach(field => delete postData[field]);

    if (Object.keys(postData).filter(k => !alwaysSend.includes(k)).length === 0 && !hasNonContactChanges) {
      this.toastrService.info(`No effective changes for ${contactType} site checks.`);
      return;
    }

    if (!postData['ladingSiteCheckUuid'] && !postData['ladingUuid']) {
      postData['ladingUuid'] = this.ladingUuid;
    }

    if (!postData['contactUuid'] && !postData['ladingSiteCheckUuid']) {
      this.toastrService.error(`Contact for ${contactType} missing.`);
      return;
    }

    this.httpClient.post<any>('/api/work_orders/lading_site_checks.php', postData).subscribe({
      next: (response) => {
        if (response?.success !== false) {
          this.toastrService.success(`${contactType} site checks saved.`);
          form.markAsPristine();
          if (!form.get('ladingSiteCheckUuid')?.value && response.lading_site_check_uuid) {
            form.get('ladingSiteCheckUuid')?.setValue(response.lading_site_check_uuid, { emitEvent: false });
          }
        } else {
          this.toastrService.error(response.message || `Error saving ${contactType} site checks.`);
        }
      },
      error: (error) => {
        console.error(`Error saving ${contactType} site checks:`, error);
        this.toastrService.error(`Error saving ${contactType} site checks.`);
      }
    });
  }

  submitShipperSiteChecks(): void {
    this.postSiteChecks(this.shipperInfoForm, 'Shipper');
  }

  submitConsigneeSiteChecks(): void {
    this.postSiteChecks(this.consigneeInfoForm, 'Consignee');
  }

  submitCustomerSiteChecks(): void {
    this.postSiteChecks(this.customerInfoForm, 'Customer');
  }

  private buildDirtyFormPayload(form: FormGroup, alwaysSendKeys: string[] = []): { [key: string]: any } {
    const payload: { [key: string]: any } = {};
    alwaysSendKeys.forEach(key => {
      const control = form.get(key);
      if (control && control.value !== null && control.value !== '') {
        payload[key] = control.value;
      } else if (control && control.value === '' && key.includes('uuid')) {
        payload[key] = null;
      }
    });

    Object.keys(form.controls).forEach(controlName => {
      const control = form.get(controlName);
      if (control && control.dirty && !payload.hasOwnProperty(controlName)) {
        let value = control.value;
        if (typeof value === 'boolean') {
          payload[controlName] = value;
        } else if (controlName.toLowerCase().endsWith('Date') && value) {
          payload[controlName] = this.convertDateForApi(value);
        } else if (value === '' && !alwaysSendKeys.includes(controlName)) {
          payload[controlName] = null;
        } else {
          payload[controlName] = value;
        }
      }
    });

    const ladingUuidControl = form.get('ladingUuid');
    if (ladingUuidControl && ladingUuidControl.value && !payload['ladingUuid']) {
      payload['ladingUuid'] = ladingUuidControl.value;
    } else if (!ladingUuidControl && this.ladingUuid && [
      this.operationInfoForm, this.customerCareInfoForm, this.salesInfoForm, this.administrationInfoForm
    ].includes(form)) {
      payload['ladingUuid'] = this.ladingUuid;
    }

    return payload;
  }

  private submitGenericForm(form: FormGroup, endpoint: string, successMessage: string, errorMessage: string, idKey: string): void {
    const idControl = form.get(idKey);
    const ladingUuidControl = form.get('ladingUuid');

    if (!form.dirty && !idControl?.value) {
      this.toastrService.info(`No changes to save for ${successMessage.split(' ')[0]}.`);
      return;
    }

    if (!form.valid) {
      this.toastrService.error(`Please correct errors before saving ${successMessage.split(' ')[0]}.`);
      form.markAllAsTouched();
      return;
    }

    const alwaysSend = [idKey];
    if (ladingUuidControl && this.ladingUuid) {
      if (!ladingUuidControl.value) {
        ladingUuidControl.setValue(this.ladingUuid, { emitEvent: false });
      }
      alwaysSend.push('ladingUuid');
    }

    const formData = this.buildDirtyFormPayload(form, Array.from(new Set(alwaysSend)));
    const formDataKeys = Object.keys(formData);
    const hasOtherChanges = formDataKeys.some(key => !alwaysSend.includes(key));

    if (!hasOtherChanges && !form.dirty && formDataKeys.length <= alwaysSend.length) {
      if (formDataKeys.length > 0 && idControl?.value) {
        // Allow save if ID exists
      } else if (!form.dirty) {
        this.toastrService.info(`No effective changes to save for ${successMessage.split(' ')[0]}.`);
        return;
      }
    }

    if (endpoint.includes('lading_') && !formData['ladingUuid'] && this.ladingUuid) {
      formData['ladingUuid'] = this.ladingUuid;
    }

    if (Object.keys(formData).length === 0) {
      this.toastrService.info(`No data to save for ${successMessage.split(' ')[0]}.`);
      return;
    }

    this.httpClient.post<any>(endpoint, formData).subscribe({
      next: (response) => {
        if (response?.success !== false) {
          this.toastrService.success(successMessage);
          form.markAsPristine();
          if (idKey && idControl && !idControl.value && response[idKey]) {
            idControl.setValue(response[idKey], { emitEvent: false });
          }
          if (ladingUuidControl && response.lading_uuid && ladingUuidControl.value !== response.lading_uuid) {
            ladingUuidControl.setValue(response.lading_uuid, { emitEvent: false });
          }
        } else {
          this.toastrService.error(response.message || errorMessage);
        }
      },
      error: (error) => {
        console.error(`Error submitting ${successMessage.split(' ')[0]} form:`, error);
        this.toastrService.error(errorMessage);
      }
    });
  }

  submitOperationInfo(): void {
    this.submitGenericForm(this.operationInfoForm, '/api/work_orders/lading_operations.php', 'Operation info saved.', 'Error saving operation info.', 'ladingOperationUuid');
  }

  get pendingLabourReviewCount(): number {
    return this.ladingLabourReviews.filter(review => this.isLabourReviewPending(review)).length;
  }

  get approvedLabourReviewCount(): number {
    return this.ladingLabourReviews.filter(review => String(review.status || '').toLowerCase() === 'approved').length;
  }

  get currentLabourReview(): LadingLabourReview | null {
    return this.ladingLabourReviews[0] || null;
  }

  labourReviewFormAt(index: number): FormGroup {
    return this.labourReviewForms.at(index) as FormGroup;
  }

  trackLabourReview(index: number, review: LadingLabourReview): string {
    return review.lading_labour_review_uuid || `${index}`;
  }

  canActOnLabourReview(review: LadingLabourReview): boolean {
    return this.isLabourReviewPending(review) && !!review.lading_labour_review_uuid;
  }

  isLabourReviewApprovalBusy(review: LadingLabourReview): boolean {
    return !!review.lading_labour_review_uuid && this.activeLabourReviewApprovalUuid === review.lading_labour_review_uuid;
  }

  labourRerateStatus(review: LadingLabourReview): string {
    if (!this.toBoolean(review.rerate_requested, false)) return 'Not requested';
    if (this.toBoolean(review.rerate_completed, false)) return 'Completed';
    return String(review.status || '').toLowerCase() === 'approved'
      ? 'Pending financial review'
      : 'Pending approval';
  }

  resetLabourReviewForm(index: number): void {
    const review = this.ladingLabourReviews[index];
    if (!review) return;
    this.labourReviewForms.setControl(index, this.createLabourReviewApprovalForm(review));
  }

  approveLabourReviewAsRequested(review: LadingLabourReview, index: number): void {
    if (!this.canActOnLabourReview(review) || this.isLabourReviewApprovalBusy(review)) return;

    const form = this.labourReviewFormAt(index);
    form.patchValue({
      approvedInstallMinutes: this.pickLabourNumber(review.requested_install_minutes, review.estimated_install_minutes, 0),
      approvedHandlingMinutes: this.pickLabourNumber(review.requested_handling_minutes, review.estimated_handling_minutes, 0),
      approvedPeople: this.pickLabourWholeNumber(review.requested_people, review.estimated_people),
      approvedNotes: 'Approved as requested.',
      rerateRequested: true
    }, { emitEvent: false });

    this.submitLabourReviewDecision(review, index, 'approved');
  }

  coerceLabourReviewPeople(index: number): void {
    const control = this.labourReviewFormAt(index).get('approvedPeople');
    if (!control) return;

    control.setValue(this.toNullableWholeNumber(control.value), { emitEvent: false });
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
  }

  submitLabourReviewDecision(review: LadingLabourReview, index: number, decision: 'approved' | 'denied'): void {
    if (!review.lading_labour_review_uuid || this.isLabourReviewApprovalBusy(review)) return;

    const form = this.labourReviewFormAt(index);
    if (form.invalid) {
      this.toastrService.error('Please correct labor approval values before saving.');
      form.markAllAsTouched();
      return;
    }

    const raw = form.getRawValue();
    const payload: { [key: string]: string | number | boolean | null } = {
      lading_labour_review_uuid: review.lading_labour_review_uuid,
      lading_uuid: review.lading_uuid || this.ladingUuid,
      decision,
      approved_notes: this.normalizeLabourNotes(raw.approvedNotes)
    };

    if (decision === 'approved') {
      payload['approved_install_minutes'] = this.toNullableNumber(raw.approvedInstallMinutes);
      payload['approved_handling_minutes'] = this.toNullableNumber(raw.approvedHandlingMinutes);
      payload['approved_people'] = this.toNullableWholeNumber(raw.approvedPeople);
      payload['rerate_requested'] = true;
    }

    this.activeLabourReviewApprovalUuid = review.lading_labour_review_uuid;

    this.httpClient.post<LabourReviewApprovalResponse>(this.labourReviewApprovalApiUrl, payload).subscribe({
      next: (response) => {
        this.activeLabourReviewApprovalUuid = null;
        if (!this.isLabourReviewApprovalSuccess(response)) {
          this.toastrService.error(this.labourReviewApprovalErrorMessage(response));
          return;
        }

        const updatedReview = response.data?.updated_review || response.raw?.data?.updated_review;
        if (updatedReview) {
          this.replaceLabourReview(index, updatedReview);
        } else {
          this.replaceLabourReview(index, {
            ...review,
            status: decision,
            approved_notes: this.normalizeLabourNotes(raw.approvedNotes)
          });
        }

        this.toastrService.success(response.message || (decision === 'approved' ? 'Labor review approved.' : 'Labor review denied.'));
      },
      error: (error) => {
        console.error('Error submitting labor review decision:', error);
        this.activeLabourReviewApprovalUuid = null;
        this.toastrService.error('Network error while submitting labor review decision.');
      }
    });
  }

  formatLabourMinutes(value: number | string | null | undefined): string {
    const minutesValue = this.toNullableNumber(value);
    if (minutesValue === null) return '-';

    const rounded = Math.round(minutesValue);
    const hours = Math.floor(rounded / 60);
    const minutes = rounded % 60;

    if (hours <= 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  }

  formatLabourWholeNumber(value: number | string | null | undefined): string {
    const numericValue = this.toNullableWholeNumber(value);
    return numericValue === null ? '-' : String(numericValue);
  }

  labourActualMinutes(review: LadingLabourReview): number | null {
    const startedMs = this.labourTimestampMillis(review.actual_started_at);
    if (startedMs === null) return null;

    const completedMs = this.labourTimestampMillis(review.actual_completed_at) ?? Date.now();
    return Math.max(0, completedMs - startedMs) / 60000;
  }

  labourAllowedMinutes(review: LadingLabourReview): number | null {
    return this.toNullableNumber(review.approved_total_minutes)
      ?? this.toNullableNumber(review.estimated_total_minutes);
  }

  labourOverMinutes(review: LadingLabourReview): number | null {
    const actual = this.labourActualMinutes(review);
    const allowed = this.labourAllowedMinutes(review);
    if (actual === null || allowed === null) return null;
    return Math.max(0, actual - allowed);
  }

  labourActualStatus(review: LadingLabourReview): string {
    if (!review.actual_started_at) return 'Not started';
    if (!review.actual_completed_at) return 'In progress';

    const over = this.labourOverMinutes(review);
    if (over !== null && over > 0) return `Over by ${this.formatLabourMinutes(over)}`;
    return 'Within allowed time';
  }

  labourActualStatusClass(review: LadingLabourReview): string {
    if (!review.actual_started_at) return 'border-slate-200 bg-slate-100 text-slate-600';
    if (!review.actual_completed_at) return 'border-amber-200 bg-amber-100 text-amber-800';

    const over = this.labourOverMinutes(review);
    return over !== null && over > 0
      ? 'border-rose-200 bg-rose-100 text-rose-800'
      : 'border-emerald-200 bg-emerald-100 text-emerald-800';
  }

  formatLabourStatus(value: string | null | undefined): string {
    if (!value) return 'Unknown';
    return value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, match => match.toUpperCase());
  }

  labourReviewTitle(review: LadingLabourReview): string {
    const requestedPeople = this.toNullableWholeNumber(review.requested_people);
    const requestedTotal = this.toNullableNumber(review.requested_total_minutes);
    const requestedDriveHours = this.toNullableNumber(review.requested_technician_drive_hours);
    const requestedHotelNights = this.toNullableWholeNumber(review.requested_technician_hotel_nights);
    const hasCrew = requestedPeople !== null && requestedPeople > 0;
    const hasTime = requestedTotal !== null && requestedTotal > 0;
    const hasTravel = (requestedDriveHours !== null && requestedDriveHours > 0)
      || (requestedHotelNights !== null && requestedHotelNights > 0);

    if ((hasCrew || hasTime) && hasTravel) return 'Labor and technician travel request';
    if (hasTravel) return 'Technician travel request';
    if (hasCrew && hasTime) return 'Crew and labor time request';
    if (hasCrew) return 'Additional crew request';
    if (hasTime) return 'Labor time request';
    return 'Labor request';
  }

  formatTechnicianDriveHours(value: number | string | null | undefined): string {
    const hours = this.toNullableNumber(value);
    if (hours === null || hours === 0) return '—';
    return `${hours.toLocaleString('en-US', { maximumFractionDigits: 2 })}h`;
  }

  formatHotelNights(value: number | string | null | undefined): string {
    const nights = this.toNullableWholeNumber(value) || 0;
    if (nights === 0) return 'No';
    return `${nights} ${nights === 1 ? 'night' : 'nights'}`;
  }

  formatLabourTimestamp(value: string | null | undefined): string {
    if (!value) return '-';

    const fromSql = DateTime.fromSQL(value, { zone: 'America/Toronto' });
    const parsed = fromSql.isValid ? fromSql : DateTime.fromISO(value);
    return parsed.isValid ? parsed.toLocaleString(DateTime.DATETIME_MED) : value;
  }

  private labourTimestampMillis(value: string | null | undefined): number | null {
    if (!value) return null;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }

  labourReviewStatusClass(review: LadingLabourReview): string {
    const status = String(review.status || '').toLowerCase();
    if (status === 'approved') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    if (status === 'denied') return 'border-rose-200 bg-rose-50 text-rose-700';
    if (status === 'pending' || status === 'requested') return 'border-amber-200 bg-amber-50 text-amber-700';
    return 'border-slate-200 bg-slate-100 text-slate-600';
  }

  labourReviewApprovalTotal(index: number): string {
    const form = this.labourReviewFormAt(index);
    const install = this.toNullableNumber(form.get('approvedInstallMinutes')?.value) || 0;
    const handling = this.toNullableNumber(form.get('approvedHandlingMinutes')?.value) || 0;
    return this.formatLabourMinutes(install + handling);
  }

  submitCustomerCareInfo(): void {
    this.submitGenericForm(this.customerCareInfoForm, '/api/work_orders/lading_care.php', 'Customer care info saved.', 'Error saving customer care info.', 'ladingCareUuid');
  }

  submitSalesInfo(): void {
    this.submitGenericForm(this.salesInfoForm, '/api/work_orders/lading_sales.php', 'Sales info saved.', 'Error saving sales info.', 'ladingSaleUuid');
  }

  submitAdministrationInfo(): void {
    this.submitGenericForm(this.administrationInfoForm, '/api/work_orders/lading_administrations.php', 'Administration info saved.', 'Error saving administration info.', 'ladingAdministrationUuid');
  }

  submitClaimInfo(): void {
    this.submitGenericForm(this.claimInfoForm, '/api/work_orders/claim_info.php', 'Claim info saved.', 'Error saving claim info.', 'loadDamageUuid');
  }

  private replaceLabourReview(index: number, review: LadingLabourReview): void {
    this.ladingLabourReviews = this.ladingLabourReviews.map((currentReview, currentIndex) =>
      currentIndex === index ? review : currentReview
    );
    this.labourReviewForms.setControl(index, this.createLabourReviewApprovalForm(review));
  }

  private isLabourReviewPending(review: LadingLabourReview): boolean {
    return ['pending', 'requested'].includes(String(review.status || '').toLowerCase());
  }

  private isLabourReviewApprovalSuccess(response: LabourReviewApprovalResponse | null | undefined): boolean {
    if (!response) return false;
    return response.success === true
      || response.code === 200
      || response.code === '200'
      || response.data?.action_result === 'success'
      || response.raw?.data?.action_result === 'success';
  }

  private labourReviewApprovalErrorMessage(response: LabourReviewApprovalResponse | null | undefined): string {
    if (!response) return 'Failed to submit labor review decision.';

    const detail = response.data?.error_message || response.raw?.data?.error_message;
    const message = response.message || 'Failed to submit labor review decision.';
    return detail ? `${message}: ${detail}` : message;
  }

  private pickLabourNumber(...values: Array<number | string | null | undefined>): number | null {
    for (const value of values) {
      const parsed = this.toNullableNumber(value);
      if (parsed !== null) return parsed;
    }
    return null;
  }

  private pickLabourWholeNumber(...values: Array<number | string | null | undefined>): number | null {
    for (const value of values) {
      const parsed = this.toNullableWholeNumber(value);
      if (parsed !== null) return parsed;
    }
    return null;
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }

  private toNullableWholeNumber(value: number | string | null | undefined): number | null {
    const parsed = this.toNullableNumber(value);
    return parsed === null ? null : Math.ceil(parsed);
  }

  private normalizeLabourNotes(value: unknown): string | null {
    const notes = String(value ?? '').trim();
    return notes ? notes : null;
  }

  private toBoolean(value: unknown, fallback = false): boolean {
    if (value === null || value === undefined || value === '') return fallback;
    if (value === true || value === 1) return true;
    if (value === false || value === 0) return false;

    const normalized = String(value).trim().toLowerCase();
    if (['true', 't', 'yes', 'y', 'on', '1'].includes(normalized)) return true;
    if (['false', 'f', 'no', 'n', 'off', '0'].includes(normalized)) return false;
    return fallback;
  }

  // Status timeline methods
  get statusForms(): FormGroup[] {
    return this.statusTimelineForms.controls as FormGroup[];
  }

  isCurrentStatus(statusForm: FormGroup): boolean {
    return statusForm.get('isCurrent')?.value === true;
  }

  formatStatusRecordedAt(statusForm: FormGroup): string {
    const value = statusForm.get('createdAt')?.value;
    if (!value) return 'Pending server timestamp';

    const recorded = DateTime.fromSQL(value, { zone: 'UTC' });
    return recorded.isValid ? recorded.toFormat('MMM d, yyyy · h:mm a') : value;
  }

  formatStatusEventAt(statusForm: FormGroup): string {
    const date = statusForm.get('eventDate')?.value;
    const time = statusForm.get('eventTime')?.value;
    const zone = statusForm.get('eventTimeZone')?.value;
    if (!date || !time) return 'Submitted event time not supplied';

    const event = DateTime.fromISO(`${date}T${time}`, { zone: zone || 'UTC' });
    return event.isValid
      ? `${event.toFormat('MMM d, yyyy · h:mm a')} · ${zone}`
      : `${date} ${time} · ${zone || 'No timezone'}`;
  }

  deleteStatus(index: number): void {
    const statusForm = this.statusTimelineForms.at(index) as FormGroup;
    const timelineUuid = statusForm.get('timelineUuid')?.value;
    const ladingUuid = statusForm.get('ladingUuid')?.value || this.ladingUuid;

    if (!timelineUuid) {
      this.statusTimelineForms.removeAt(index);
      this.originalStatusValues.splice(index, 1);
      this.toastrService.info('Unsaved status removed.');
      return;
    }

    if (!ladingUuid) {
      this.toastrService.error('Lading UUID missing.');
      return;
    }

    if (this.isCurrentStatus(statusForm)) {
      this.toastrService.warning('Add a replacement status before deleting the current event.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete status event?',
        message: 'Delete this historical status event? This cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Keep status',
        tone: 'danger'
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.workOrderService.deleteStatusTimeline(ladingUuid, timelineUuid).subscribe({
          next: (response) => {
            if (this.statusMutationSucceeded(response)) {
              this.toastrService.success('Status deleted successfully.');
              this.resetStatusMapState();
              this.loadData();
            } else {
              this.toastrService.error(response.message || 'Could not delete status.');
            }
          },
          error: (error) => {
            console.error('Error deleting status:', error);
            this.toastrService.error('Error deleting status.');
          }
        });
      }
    });
  }

  cancelStatusChange(index: number): void {
    const statusForm = this.statusTimelineForms.at(index) as FormGroup;
    const originalValue = this.originalStatusValues[index];

    if (!originalValue || !originalValue.timelineUuid) {
      this.statusTimelineForms.removeAt(index);
      this.originalStatusValues.splice(index, 1);
    } else {
      statusForm.reset(originalValue);
      this.setLocalTimeControlsState(statusForm, originalValue.localOverride);
      statusForm.markAsPristine();
    }
  }

  addNewStatus(): void {
    if (this.statusForms.some(form => !form.get('timelineUuid')?.value)) {
      this.toastrService.info('Finish or cancel the unsaved status first.');
      return;
    }

    const now = DateTime.local().setZone(this.browserTimezone);
    const consigneeNow = now.setZone(this.consigneeTimezone || 'America/Toronto');
    const newStatus: StatusTimelineFormValue = {
      status: '',
      createdAt: null,
      modifiedAt: null,
      eventDate: now.toFormat('yyyy-MM-dd'),
      eventTime: now.toFormat('HH:mm'),
      eventTimeZone: this.browserTimezone,
      dateLocal: consigneeNow.toFormat('yyyy-MM-dd'),
      timeLocal: consigneeNow.toFormat('HH:mm'),
      localTimeZone: this.consigneeTimezone || 'America/Toronto',
      localOverride: false,
      ladingUuid: this.ladingUuid,
      timelineUuid: null,
      note: null,
      isCurrent: false
    };

    this.originalStatusValues.unshift(newStatus);
    this.statusTimelineForms.insert(0, this.createStatusTimelineForm(newStatus));
  }

  submitStatus(index: number): void {
    const statusForm = this.statusTimelineForms.at(index) as FormGroup;
    if (!statusForm.valid) {
      this.toastrService.warning('Please fill out all required fields for the status.');
      statusForm.markAllAsTouched();
      return;
    }

    const formValue = statusForm.getRawValue() as StatusTimelineFormValue;
    const eventDateTime = DateTime.fromISO(
      `${formValue.eventDate}T${formValue.eventTime}`,
      { zone: formValue.eventTimeZone }
    );
    if (!eventDateTime.isValid) {
      this.toastrService.error('The submitted event date, time, or timezone is invalid.');
      return;
    }

    if (formValue.localOverride && (!formValue.dateLocal || !formValue.timeLocal)) {
      this.toastrService.error('The consignee local override requires both a date and time.');
      return;
    }
  
    const ladingUuid = formValue.ladingUuid || this.ladingUuid;
    if (!ladingUuid) {
      this.toastrService.error('Lading UUID missing.');
      return;
    }

    const postData: WorkOrderStatusTimelinePayload = {
      lading_status: formValue.status,
      lading_uuid: ladingUuid,
      lading_status_timeline_uuid: formValue.timelineUuid || undefined,
      event_date: formValue.eventDate!,
      event_time: formValue.eventTime!,
      event_timezone: formValue.eventTimeZone,
      override_local_time: formValue.localOverride,
      consignee_local_date: formValue.localOverride ? formValue.dateLocal! : undefined,
      consignee_local_time: formValue.localOverride ? formValue.timeLocal! : undefined,
      lading_status_note: formValue.note
    };

    this.prepareStatusSave(statusForm, postData);
  }

  private prepareStatusSave(
    statusForm: FormGroup,
    postData: WorkOrderStatusTimelinePayload
  ): void {
    if (postData.lading_status !== this.deliveredConsigneeStatus) {
      this.saveStatus(statusForm, postData, false);
      return;
    }

    this.workOrderService.getLadingProductDeliveryPreview(postData.lading_uuid).subscribe({
      next: (response) => {
        if (!this.productDeliveryResponseSucceeded(response)) {
          this.toastrService.error(
            response?.message || 'Could not check product delivery timestamps. The status was not saved.'
          );
          return;
        }

        const missingCount = this.productDeliveryResponseCount(response.missing_delivery_count);
        if (missingCount === 0) {
          this.saveStatus(statusForm, postData, false);
          return;
        }

        if (response.is_final_leg === false) {
          const finalLegLabel = response.selected_final_work_order
            ? ` The final leg is ${response.selected_final_work_order}.`
            : '';
          this.toastrService.warning(
            `Products can only be marked delivered from the final leg of the order.${finalLegLabel}`
          );
          this.saveStatus(statusForm, postData, false);
          return;
        }

        const eligibleMissingCount = response.eligible_missing_delivery_count === undefined
          ? missingCount
          : this.productDeliveryResponseCount(response.eligible_missing_delivery_count);
        const blockedMissingCount = this.productDeliveryResponseCount(response.blocked_missing_delivery_count);

        if (eligibleMissingCount === 0) {
          const blockedLabel = blockedMissingCount === 1 ? 'product has' : 'products have';
          this.toastrService.warning(
            `${blockedMissingCount} ${blockedLabel} no delivery time but cannot be marked delivered until received in and audited.`
          );
          this.saveStatus(statusForm, postData, false);
          return;
        }

        const productCount = this.productDeliveryResponseCount(response.product_count);
        const deliveryDateTime = postData.override_local_time
          ? DateTime.fromISO(
              `${postData.consignee_local_date}T${postData.consignee_local_time}`,
              { zone: this.consigneeTimezone || 'America/Toronto' }
            )
          : DateTime.fromISO(
              `${postData.event_date}T${postData.event_time}`,
              { zone: postData.event_timezone }
            );
        const deliveryTimeLabel = deliveryDateTime.isValid
          ? `${deliveryDateTime.toLocaleString(DateTime.DATETIME_MED)} (${deliveryDateTime.zoneName})`
          : `${postData.event_date} ${postData.event_time}`;
        const productLabel = eligibleMissingCount === 1 ? 'product' : 'products';
        const totalLabel = productCount > missingCount
          ? ` out of ${productCount} products`
          : '';
        const blockedMessage = blockedMissingCount > 0
          ? `\n\n${blockedMissingCount} ${blockedMissingCount === 1 ? 'product is' : 'products are'} not both received in and audited and will remain unchanged.`
          : '';

        this.dialog.open(ConfirmDialogComponent, {
          data: {
            title: 'Mark audited products delivered?',
            message: `This lading has ${missingCount} ${missingCount === 1 ? 'product' : 'products'}${totalLabel} without a delivery time. ${eligibleMissingCount} ${productLabel} ${eligibleMissingCount === 1 ? 'has' : 'have'} been received in and audited.\n\nMark ${eligibleMissingCount === 1 ? 'it' : 'them'} delivered at ${deliveryTimeLabel}? Existing delivery times will not be changed.${blockedMessage}`,
            confirmText: 'Mark delivered',
            cancelText: 'Status only',
            tone: 'warning'
          }
        }).afterClosed().subscribe(confirmed => {
          this.saveStatus(statusForm, postData, confirmed === true);
        });
      },
      error: (error) => {
        console.error('Error checking product delivery timestamps:', error);
        this.toastrService.error('Could not check product delivery timestamps. The status was not saved.');
      }
    });
  }

  private saveStatus(
    statusForm: FormGroup,
    postData: WorkOrderStatusTimelinePayload,
    markProductsDelivered: boolean
  ): void {
    this.statusSaveInProgress = true;
    const request = postData.lading_status_timeline_uuid
      ? this.workOrderService.updateStatusTimeline(postData)
      : this.workOrderService.createStatusTimeline(postData);

    request.subscribe({
      next: (response) => {
        if (!this.statusMutationSucceeded(response)) {
          this.statusSaveInProgress = false;
          this.toastrService.error(response.message || 'Error saving status.');
          return;
        }

        statusForm.markAsPristine();
        this.toastrService.success(response.message || 'Status saved successfully.');

        if (!markProductsDelivered) {
          this.finishStatusSave();
          return;
        }

        const deliveredTimeUtc = this.statusDeliveryTimeUtc(response.data);
        if (!deliveredTimeUtc) {
          this.toastrService.error('The status was saved, but its delivery time could not be converted for the products.');
          this.finishStatusSave();
          return;
        }

        this.markProductsDelivered(postData.lading_uuid, deliveredTimeUtc, () => this.finishStatusSave());
      },
      error: (error) => {
        this.statusSaveInProgress = false;
        console.error('Error saving status:', error);
        this.toastrService.error('Error saving status.');
      }
    });
  }

  private statusDeliveryTimeUtc(savedStatus: StatusTimelineInfo | undefined): string | null {
    if (!savedStatus?.local_time || !savedStatus.time_zone_id) return null;

    const deliveryTime = DateTime.fromISO(
      savedStatus.local_time.replace(' ', 'T'),
      { zone: savedStatus.time_zone_id }
    );
    return deliveryTime.isValid ? deliveryTime.toUTC().toFormat('yyyy-MM-dd HH:mm:ss') : null;
  }

  private markProductsDelivered(ladingUuid: string, deliveredTimeUtc: string, complete: () => void): void {
    this.workOrderService.markLadingProductsDelivered(ladingUuid, deliveredTimeUtc).subscribe({
      next: (response) => {
        if (!this.productDeliveryResponseSucceeded(response)) {
          this.toastrService.error(
            response?.message || 'The status was saved, but the products could not be marked delivered.'
          );
          complete();
          return;
        }

        const updatedCount = this.productDeliveryResponseCount(response.updated_count);
        const blockedCount = this.productDeliveryResponseCount(response.blocked_missing_delivery_count);
        if (updatedCount > 0) {
          const productLabel = updatedCount === 1 ? 'product was' : 'products were';
          this.toastrService.success(`${updatedCount} ${productLabel} marked delivered.`);
        }
        if (blockedCount > 0) {
          const blockedLabel = blockedCount === 1 ? 'product was' : 'products were';
          this.toastrService.warning(
            `${blockedCount} ${blockedLabel} left unchanged because both received in and audited are required.`
          );
        }
        complete();
      },
      error: (error) => {
        console.error('Error marking products delivered:', error);
        this.toastrService.error('The status was saved, but the products could not be marked delivered.');
        complete();
      }
    });
  }

  private finishStatusSave(): void {
    this.statusSaveInProgress = false;
    this.resetStatusMapState();
    this.loadData();
  }

  private statusMutationSucceeded(
    response: WorkOrderStatusTimelineResponse | null | undefined
  ): boolean {
    return response?.success === true || String(response?.code) === '200';
  }

  private productDeliveryResponseSucceeded(response: LadingProductDeliveryResponse | null | undefined): boolean {
    return response?.success === true || String(response?.code) === '200';
  }

  private productDeliveryResponseCount(value: string | number | null | undefined): number {
    const count = Number(value);
    return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  }

  private refreshStatusMap(): void {
    this.resetStatusMapState();
    if (this.statusTabActive) {
      this.loadStatusMapData();
    }
  }

  private resetStatusMapState(): void {
    this.statusMapLoaded = false;
    this.statusMapPoints = [];
    if (this.statusMap) {
      this.statusMap.remove();
      this.statusMap = null;
    }
  }

  private loadStatusMapData(): void {
    if (!this.ladingUuid) {
      this.statusMapLoaded = true;
      this.statusMapPoints = [];
      return;
    }

    if (this.statusMapLoaded && this.statusMapPoints.length > 0 && this.statusMap) {
      setTimeout(() => this.statusMap?.invalidateSize(), 120);
      return;
    }

    this.httpClient.post<{ success: boolean; data: StatusMapPoint[] }>(this.statusMapApiUrl, {
      lading_uuid: this.ladingUuid
    }).subscribe({
      next: (response) => {
        this.statusMapLoaded = true;
        this.statusMapPoints = response?.success ? (response.data || []) : [];
        if (this.statusMapPoints.length > 0) {
          setTimeout(() => this.initStatusMap(), 120);
        }
      },
      error: (error) => {
        console.error('Error loading status map:', error);
        this.statusMapLoaded = true;
        this.statusMapPoints = [];
        this.toastrService.error('Error loading status map.');
      }
    });
  }

  private initStatusMap(): void {
    if (!this.statusMapContainer || this.statusMapPoints.length === 0) return;

    if (this.statusMap) {
      this.statusMap.remove();
      this.statusMap = null;
    }

    this.statusMap = L.map(this.statusMapContainer.nativeElement).setView([39.8283, -98.5795], 4);
    const map = this.statusMap;

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);

    const latLngs: L.LatLngExpression[] = [];

    this.statusMapPoints.forEach((point, index) => {
      latLngs.push([point.lat, point.lng]);

      const isLatest = index === 0;
      const customIcon = L.divIcon({
        className: 'custom-leaflet-icon',
        html: `<div class="${isLatest ? 'map-dot-active' : 'map-dot-history'}"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });

      L.marker([point.lat, point.lng], { icon: customIcon })
        .addTo(map)
        .bindPopup(`
          <div class="map-popup">
            <strong>${point.status}</strong><br>
            <span class="text-muted">${this.formatStatusMapTimestamp(point.time)}</span>
          </div>
        `);
    });

    if (latLngs.length > 1) {
      L.polyline(latLngs, { color: '#0f172a', weight: 3, dashArray: '6, 6' }).addTo(map);
    }

    map.fitBounds(L.latLngBounds(latLngs), { padding: [32, 32] });
  }

  private formatStatusMapTimestamp(timestamp: string | null | undefined): string {
    if (!timestamp) return 'Unknown time';
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime())
      ? timestamp
      : parsed.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        });
  }

  // Contact change dialogs
  private openChangeDialog(component: any, callback: (result: any) => void): void {
    this.dialog.open(component, { width: '450px', data: { current_lading_uuid: this.ladingUuid } })
      .afterClosed()
      .subscribe(result => {
        if (result?.contact_uuid) callback(result);
      });
  }

  private submitNewContactInfo(type: 'shipper' | 'consignee' | 'carrier', formData: any): void {
    if (!this.ladingUuid) return;

    const payload = { contact_uuid: formData.contact_uuid };
    this.httpClient.post<any>(`/api/work_orders/set_${type}.php?lading_uuid=${this.ladingUuid}`, payload).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastrService.success(`${type.charAt(0).toUpperCase() + type.slice(1)} updated.`);
          this.loadData();
        } else {
          this.toastrService.error(response.message || `Failed to update ${type}.`);
        }
      },
      error: (error) => {
        console.error(`Error updating ${type}:`, error);
        this.toastrService.error(`Error updating ${type}.`);
      }
    });
  }

  openChangingShipperDialog(): void {
    const dialogRef = this.dialog.open(ChangingShipperDialogComponent, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const postData = {
          lading_uuid: this.ladingUuid,
          shipper_contact_uuid: result.shipper_contact_uuid,
          shipper_name: result.shipper_name,
          shipper_locality: result.shipper_locality,
          shipper_region: result.shipper_region,
          shipper_contact_address_uuid: result.shipper_address_uuid
        };

        this.httpClient.post('/api/work_orders/change_shipper.php', postData).subscribe(response => {
          console.log('Shipper changed:', response);
          this.toastrService.success('Shipper updated successfully.');
          window.location.reload();
        }, error => {
          console.error('Error changing shipper:', error);
          this.toastrService.error('Failed to update shipper.');
        });
      }
    });
  }

  submitNewShipperInfo(formData?: any): void {
    if (formData?.contact_uuid) {
      this.submitNewContactInfo('shipper', formData);
    } else {
      this.submitShipperSiteChecks();
    }
  }

  openChangingConsigneeDialog(): void {
    const dialogRef = this.dialog.open(ChangingConsigneeDialogComponent, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const postData = {
          lading_uuid: this.ladingUuid,
          consignee_contact_uuid: result.consignee_contact_uuid,
          consignee_name: result.consignee_name,
          consignee_locality: result.consignee_locality,
          consignee_region: result.consignee_region,
          consignee_contact_address_uuid: result.consignee_address_uuid
        };

        console.log(postData);

        this.httpClient.post('/api/work_orders/change_consignee.php', postData).subscribe(response => {
          console.log('Consignee changed:', response);
          this.toastrService.success('Consignee updated successfully');
          window.location.reload();
        }, error => {
          console.error('Error changing consignee:', error);
          this.toastrService.error('Failed to update consignee.');
        })
      }
    })
  }

  submitNewConsigneeInfo(formData?: any): void {
    if (formData?.contact_uuid) {
      this.submitNewContactInfo('consignee', formData);
    } else {
      this.submitConsigneeSiteChecks();
    }
  }

  openChangingCarrierDialog(): void {
    const dialogRef = this.dialog.open(ChangingCarrierDialogComponent, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const postData = {
          lading_uuid: this.ladingUuid,
          carrier_contact_uuid: result.carrier_contact_uuid,
          carrier_name: result.carrier_name,
          carrier_locality: result.carrier_locality
        };

        this.httpClient.post('/api/work_orders/change_carrier.php', postData).subscribe(response => {
          console.log('Carrier changed:', response);
          this.toastrService.success('Carrier updated successfully.');
          window.location.reload();
        }, error => {
          console.error('Error changing carrier:', error);
          this.toastrService.error('Failed to update carrier.');
        });
      }
    });
  }

  submitNewCarrierInfo(formData?: any): void {
    if (formData?.contact_uuid) {
      this.submitNewContactInfo('carrier', formData);
    }
  }

  // Utility methods
  private preventDefaultDragAndDrop(): void {
    window.addEventListener('dragover', e => e.preventDefault());
    window.addEventListener('drop', e => e.preventDefault());
  }

  private setupFormValueChanges(): void {
    this.generalInfoForm.get('carrierTrailer')?.valueChanges.subscribe(value => {
      if (value && typeof value === 'string') {
        const formatted = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (formatted !== value) {
          this.generalInfoForm.get('carrierTrailer')?.setValue(formatted, { emitEvent: false });
        }
      }
    });
  }

  private convertDbTimeToDisplay(easternDateTime: string, targetTimezone: string): { date: string; time: string } {
    if (!easternDateTime || !targetTimezone) return { date: '', time: '' };

    try {
      // Account for timestamptz, which puts UTC offset after seconds
      if (easternDateTime.charAt(19) == '-' || easternDateTime.charAt(19) == '+'){
        easternDateTime = easternDateTime.slice(0,19);
      }
      const dateTime = DateTime.fromFormat(easternDateTime, 'yyyy-MM-dd HH:mm:ss', { zone: 'America/Toronto' });
      if (!dateTime.isValid) {
        const dateOnly = easternDateTime.split(' ')[0];
        const fallbackDate = DateTime.fromISO(dateOnly, { zone: 'America/Toronto' });
        if (fallbackDate.isValid) {
          return {
            date: fallbackDate.setZone(targetTimezone).toFormat('yyyy-MM-dd'),
            time: '00:00'
          };
        }
        return { date: '', time: '' };
      }
      return {
        date: dateTime.setZone(targetTimezone).toFormat('yyyy-MM-dd'),
        time: dateTime.setZone(targetTimezone).toFormat('HH:mm')
      };
    } catch (error) {
      console.error('Error converting DB time:', error, { easternDateTime, targetTimezone });
      return { date: '', time: '' };
    }
  }

  private convertDisplayToDbTime(localDate: string, localTime: string, sourceTimezone: string): string | null {
    if (!localDate || !localTime || !sourceTimezone) return null;

    const correctedTime = localTime.length === 5 ? `${localTime}:00` : localTime;
    try {
      const dateTime = DateTime.fromFormat(`${localDate} ${correctedTime}`, 'yyyy-MM-dd HH:mm:ss', { zone: sourceTimezone });
      if (!dateTime.isValid) return null;
      return dateTime.setZone('America/Toronto').toFormat('yyyy-MM-dd HH:mm:ss');
    } catch (error) {
      return null;
    }
  }

  private formatAddressText(addresses: AddressInfo[], name: string): string {
    if (!addresses || addresses.length === 0) return `${name} - Address not available.`;

    return addresses.map(address => {
      return `${name} - ${address.address_type || 'Primary'}\n` +
             `${address.address_street || ''}\n` +
             `${address.address_extended ? `${address.address_extended}\n` : ''}` +
             `${address.address_locality || ''}, ${address.address_region || ''} ${address.address_postal_code || ''}\n` +
             `${address.address_country || ''}`;
    }).join('\n\n');
  }

  private convertDateForApi(date: any): string {
    if (!date) return '';
    try {
      if (date instanceof Date) {
        return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
      }
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      return DateTime.fromISO(new Date(date).toISOString()).toFormat('yyyy-MM-dd');
    } catch {
      return '';
    }
  }

  // Navigation and actions
  getCarrierDetails(): void {
    const contactUuid = this.carrierInfoForm.get('contactUuid')?.value;
    if (contactUuid) {
      this.router.navigate(['/contacts'], { queryParams: { contact_uuid: contactUuid } });
    }
  }

  getShipperDetails(): void {
    const contactUuid = this.shipperInfoForm.get('contactUuid')?.value;
    if (contactUuid) {
      this.router.navigate(['/contacts'], { queryParams: { contact_uuid: contactUuid } });
    }
  }

  getConsigneeDetails(): void {
    const contactUuid = this.consigneeInfoForm.get('contactUuid')?.value;
    if (contactUuid) {
      this.router.navigate(['/contacts'], { queryParams: { contact_uuid: contactUuid } });
    }
  }

  promptEmail(contactType: string): void {
    const email = prompt(`Email ${contactType} confirmation to:`);
    if (email) {
      this.toastrService.info(`Email to ${email} TBD.`);
    }
  }

  printPdf(): void {
    const ladingUuid = this.generalInfoForm.get('ladingUuid')?.value;
    if (ladingUuid) {
      window.open(`https://ams.ulaap.com/pages/work_orders/carrier_confirmation.php?uuid=${ladingUuid}`, '_blank');
    } else {
      this.toastrService.error('Lading UUID missing.');
    }
  }

  navigateTo(linkKey: string): void {
    const url = this.installationFormLinks[linkKey];
    if (url && (url.includes('?token=') || (!url.includes('ulaap.com/') && !url.includes('?token=')))) {
      window.open(url, '_blank');
    } else if (url && url.includes('ulaap.com/') && !url.includes('?token=')) {
      this.toastrService.warning('Token for this form is not available.', 'Link Token Missing');
    } else {
      this.toastrService.warning(`Link not available for: ${linkKey}`, 'Navigation Error');
    }
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  addNote(): void {
    this.ladingNotes.unshift({ note: '', contact_uuid: this.defaultNoteContactUuid });
    setTimeout(() => {
      const noteTextarea = this.noteTextareas?.first?.nativeElement;
      if (!noteTextarea) return;
      noteTextarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      noteTextarea.focus();
    });
  }

  deleteNote(index: number): void {
    const note = this.ladingNotes[index];
    if (!note) return;

    this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { title: 'Confirm Deletion', message: 'Delete this note?' }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        if (note.lading_note_uuid) {
          this.httpClient.post<any>('/api/work_orders/details/process_lading_notes.php', {
            lading_uuid: this.ladingUuid,
            notes: [{ lading_note_uuid: note.lading_note_uuid, note: '' }]
          }).subscribe({
            next: (response) => {
              if (response?.success) {
                this.ladingNotes.splice(index, 1);
                this.toastrService.success('Note deleted.');
              } else {
                this.toastrService.error(response.message || 'Error deleting note.');
              }
            },
            error: (error) => {
              console.error('Error deleting note:', error);
              this.toastrService.error('Error deleting note.');
            }
          });
        } else {
          this.ladingNotes.splice(index, 1);
          this.toastrService.success('Unsaved note removed.');
        }
      }
    });
  }


async viewProductImage(image: any, index: number) {
  try {
    // Fetch the full-size image for the clicked one
    const resp = await this.httpClient
      .get<any>(`/api/work_orders/lading_product_images.php?lading_image_uuid=${image.lading_image_uuid}`)
      .toPromise();

    const byteArray = new Uint8Array(
      window.atob(resp.image_data).split("").map(c => c.charCodeAt(0))
    );

    const blob = new Blob([byteArray], { type: resp.image_format });
    const fullImageUrl = URL.createObjectURL(blob);

    // Build thumbnail/placeholder arrays
    const thumbnails: string[] = [];
    const images: string[] = [];

    this.productImagesTable.data.forEach((img: any) => {
      thumbnails.push(img.thumbnail);
      images.push(img.thumbnail); // placeholder, full image replaces one below
    });

    // Replace clicked index with REAL full-size image
    images[index] = fullImageUrl;

    // OPEN MODAL WITH GALLERY DATA
    this.dialog.open(DocumentModalComponent, {
      width: '90vw',
      data: {
        type: 'product',
        product_code: image.product_code,
        images,
        thumbnails,
        index,
        originalList: this.productImagesTable.data  // <<-- REQUIRED for all the images uyuids!
      }
    });

  } catch (err) {
    console.error("Error loading product image:", err);
    this.toastrService.error("Could not load image.");
  }
}


deleteProductImage(img: any) {
  if (!confirm("Delete this image?")) return;

  this.httpClient
    .delete(`/api/work_orders/lading_product_images.php?lading_image_uuid=${img.lading_image_uuid}`)
    .subscribe({
      next: (response: any) => {
        if (response.success) {
          this.toastrService.success("Image deleted.");
          console.log(response)
          //this.loadData();
        } else {
          this.toastrService.error(response.error || "Could not delete image.");
        }
      },
      error: (err) => {
        console.error(err);
        this.toastrService.error("Error deleting image.");
      }
    });
}


  
  private hasUnsavedQualificationChanges(): boolean {
    return [this.generalInfoForm, this.shipperInfoForm, this.consigneeInfoForm,
      this.customerInfoForm, this.carrierInfoForm, this.operationInfoForm,
      this.customerCareInfoForm, this.salesInfoForm, this.administrationInfoForm,
      this.claimInfoForm, this.technicianInfoForm, this.statusTimelineForms]
      .some(form => form?.dirty);
  }

  qualifyOrder(): void {
    if (this.qualificationInProgress || !this.ladingUuid || this.alternate_reference) return;
    if (this.hasUnsavedQualificationChanges()) {
      this.toastrService.warning('Save your order changes before qualifying. Qualification uses the saved carrier and stop addresses.');
      return;
    }
    this.qualificationInProgress = true;
    this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      data: { title: 'Qualify Order', message: 'Send this order to UFOs? With a saved carrier it will be Booked; without a carrier it will be Unassigned. The alternate reference appears after confirmation.' }
    }).afterClosed().pipe(take(1)).subscribe(confirmed => {
      if (!confirmed || this.hasUnsavedQualificationChanges()) {
        this.qualificationInProgress = false;
        return;
      }
      this.qualificationMessage = 'Waiting for UFOs confirmation…';
      this.httpClient.post<QualificationResponse>('/api/work_orders/Get_UFOS_work_order_no.php', {
        lading_uuid: this.ladingUuid
      }).pipe(finalize(() => this.qualificationInProgress = false)).subscribe({
        next: response => this.handleQualificationResponse(response),
        error: error => this.handleQualificationResponse(error?.error)
      });
    });
  }

  private handleQualificationResponse(response?: QualificationResponse): void {
    const notice = qualificationNotice(response);
    this.qualificationMessage = notice.message;
    if (notice.kind === 'success') {
      // This reference comes only from a verified native receipt published in AMS.
      this.alternate_reference = response!.alternate_reference!;
      this.generalInfoForm.patchValue({ alternateReference: this.alternate_reference }, { emitEvent: false });
      this.toastrService.success(notice.message);
      if (!this.hasUnsavedQualificationChanges()) void this.loadData();
    } else if (notice.kind === 'pending') {
      this.toastrService.info(notice.message);
    } else {
      this.toastrService.error(notice.message);
    }
  }

  /* Contact Changing */
  onShipperSelected(opt: ContactAutocompleteResult): void {
    this.shipperInfoForm.patchValue({
      contactUuid: opt.value,
      contactOrganization: opt.label
    });
  }
}
